// ── Myshield Academy Content ─────────────────────────────────────────────────
// 4 tracks × 5 modules × 5 questions
// Reading content uses \n\n for paragraph breaks and **bold** for emphasis.

export interface AcademyQuestion {
  question: string;
  options: [string, string, string, string]; // A, B, C, D
  correct: 0 | 1 | 2 | 3;                   // index of correct option
  explanation: string;
}

export interface AcademyModuleData {
  index: number;
  title: string;
  readingContent: string;
  manualContent: string;
  questions: AcademyQuestion[];
}

export interface AcademyTrack {
  trackName: string;
  role: string;
  modules: AcademyModuleData[];
}

// ─────────────────────────────────────────────────────────────────────────────
// TRACK 0 — Fleet Portal User (Client role)
// ─────────────────────────────────────────────────────────────────────────────
const clientTrack: AcademyTrack = {
  trackName: 'Fleet Portal User',
  role: 'Client',
  modules: [
    {
      index: 0,
      title: 'Getting Started',
      manualContent: `**FULL TRAINING GUIDE — Getting Started with the Myshield Fleet Portal**

This guide walks you through every step of accessing, navigating, and securing your Myshield session. Read it fully before taking the quiz.

**1. ACCESSING THE PORTAL**

Open Chrome, Edge, Firefox, or Safari and navigate to your Myshield portal URL supplied by your account manager. If the page does not load, check your internet connection and try refreshing. If unavailable for more than 30 minutes contact your Myshield handler.

**2. LOGGING IN — STEP BY STEP**

Step 1 — Enter your username or email address. Your username was provided when your account was created. It is case-sensitive — check for capital letters and spaces.

Step 2 — Enter your password. Passwords are case-sensitive. Make sure Caps Lock is off.

Step 3 — Click Sign In. The system validates your credentials against the secure user database.

Step 4 — If credentials are correct, a six-digit MFA code is immediately sent to your registered email address. This step is mandatory and cannot be bypassed.

Step 5 — Open your email and locate the message from Myshield (subject: Your Myshield Verification Code). Codes arrive within 30 seconds. Check your spam folder if it does not appear.

Step 6 — Enter the six-digit code in the verification field and click Verify.

**3. MFA TROUBLESHOOTING**

Code not arriving: Check your spam/junk folder. If still missing after 2 minutes, click Resend Code on the verification screen.

Code expired: Each code is valid for exactly 10 minutes. If it has expired, click Resend Code. Only the most recently issued code is valid — do not reuse old codes.

Invalid code error: Double-check you have typed all six digits correctly. Click Resend Code if needed.

Account locked: After 5 consecutive failed attempts your account locks automatically. You will see a message saying to contact Myshield. You cannot unlock it yourself — contact your Fleet Manager or Myshield handler.

Forgotten password: Click Forgot Password on the login page. A reset link valid for 1 hour is sent to your registered email. Follow the link, enter your new password twice, and sign in again.

**4. FIRST LOGIN — GDPR CONSENT**

On your very first login the portal displays the Data Privacy and Consent Notice. This explains how Myshield and Yourshield Ltd collect and use your fleet and personal data under GDPR. You must read and click Accept before accessing any features. A permanent timestamped record of your acceptance is saved to Consent Records and cannot be altered.

**5. THE DASHBOARD**

After login you land on the Dashboard — your home screen. It shows: Total Vehicles on your policy; On Cover (active, confirmed insurance); Pending Cover (submitted, awaiting approval); Inactive (removed from policy); and a Recent Activity feed of changes and notices. Check the Dashboard every time you log in before doing any other work.

**6. NAVIGATING THE SIDEBAR**

The left sidebar is your main navigation. My Feed contains My Notices (all cover decisions and policy updates) and Messages. My Fleet contains Vehicles, Sites, and Named Drivers. Admin Toolkit contains Consent Records. Academy is your training programme. Click any section heading to expand or collapse it.

**7. SESSION SECURITY AND INACTIVITY**

Your session expires after 30 minutes of inactivity. At 25 minutes a warning modal appears — click Stay Logged In to continue. If you do not respond within 5 minutes you are automatically logged out. Always click Logout in the top-right when finished, especially on shared devices. Never share your password — each user must have their own account.

**WHAT TO DO IF YOU CANNOT LOG IN**

Cannot remember username — contact your Myshield handler. Forgotten password — use Forgot Password on the login page. MFA code not arriving — check spam, then click Resend Code. Account locked — contact your Fleet Manager or handler. Portal unavailable — check your internet, then contact Myshield support.`,
      readingContent: `Welcome to the Myshield Fleet Portal. To log in, enter your **username or email address** and password on the login page. After entering your credentials, the portal will send a **six-digit MFA (Multi-Factor Authentication) code** to your registered email address. Enter this code when prompted to complete your sign-in. MFA codes expire after **10 minutes** — if yours expires, simply request a new one.

Once logged in, you will see the **main sidebar** on the left. The sidebar contains three main areas: **My Feed** (your notices and updates), **My Fleet** (vehicles, sites, and drivers), and **Admin Toolkit** (consent records and related tools). Use the sidebar to navigate between sections.

The **Dashboard** is your home screen. It gives you a quick snapshot of your fleet: total vehicle count, how many are On Cover, Pending Cover, or Inactive, and any recent changes to your policy. It is the best place to get an overview of your fleet's current status at a glance.

For security, your portal session will **automatically expire after 30 minutes of inactivity**. You will be logged out and will need to sign in again. Always log out when you have finished using the portal, especially on shared devices. Your data is protected at all times.`,
      questions: [
        {
          question: 'What is MFA (Multi-Factor Authentication)?',
          options: [
            'A type of vehicle cover',
            'A 6-digit security code sent to your email',
            'Your username',
            'A cover letter',
          ],
          correct: 1,
          explanation: 'MFA (Multi-Factor Authentication) sends a 6-digit code to your email to verify your identity before granting access.',
        },
        {
          question: 'Where do you see your fleet overview?',
          options: ['Messages', 'Sites', 'Dashboard', 'Admin Toolkit'],
          correct: 2,
          explanation: 'The Dashboard is your home screen and shows a live overview of your entire fleet including cover statuses and recent changes.',
        },
        {
          question: 'How long does an MFA code remain valid?',
          options: ['5 minutes', '30 minutes', '10 minutes', '1 hour'],
          correct: 2,
          explanation: 'MFA codes expire after 10 minutes. If yours has expired, simply request a new one from the login screen.',
        },
        {
          question: 'Where do you find your cover decision notices?',
          options: ['Dashboard', 'My Notices in My Feed', 'Sites', 'Reports'],
          correct: 1,
          explanation: 'My Notices (found in My Feed) is where all decisions about your fleet submissions are communicated.',
        },
{
          question: 'What happens if you are inactive in the portal for 30 minutes?',
          options: [
            'Nothing',
            'Your session expires and you are logged out',
            'A warning appears only',
            'Your fleet reports are deleted',
          ],
          correct: 1,
          explanation: 'For security, inactive sessions automatically expire after 30 minutes. You will need to log in again to continue.',
        },
      ],
    },
    {
      index: 1,
      title: 'Your Fleet Vehicles',
      manualContent: `**FULL TRAINING GUIDE — Your Fleet Vehicles**

This guide covers every step of adding and managing fleet vehicles, understanding cover statuses, and what to do when something is wrong.

**1. NAVIGATING TO VEHICLES**

From the sidebar click My Fleet then click Vehicles. The Vehicles page lists every vehicle on your fleet showing registration, make, model, cover status, declared value, and site. Use the search bar to find a vehicle by registration number.

**2. ADDING A NEW VEHICLE — STEP BY STEP**

Step 1 — Click Add Vehicle in the top right of the Vehicles page.

Step 2 — Type the full registration number in the Registration field (e.g. AB12 CDE). The system will normalise the format automatically.

Step 3 — Click DVLA Lookup. The system queries the DVLA database and automatically fills in Make, Model, Year of Registration, and Vehicle Type. Always review these details for accuracy. If the lookup returns incorrect results (common with personalised plates or brand-new registrations) you can edit the fields manually.

Step 4 — If the DVLA lookup returns no result, verify the registration is correct. Very recently registered vehicles may not yet be on the DVLA database — fill in the details manually in that case.

Step 5 — Enter the vehicle's Declared Value. Use the current market value without a pound sign or commas. This figure is used by the insurer for policy rating and claims purposes. Under-declaring a value can affect a claim settlement — always use accurate market values.

Step 6 — If your fleet uses multiple sites, select the appropriate site from the dropdown. This tells the insurer where the vehicle is principally kept.

Step 7 — Click Submit. Your submission is sent to your Myshield handler for review and approval.

**3. WHAT HAPPENS AFTER SUBMISSION**

Your vehicle will show as Pending Cover immediately after submission. Your Myshield handler will review and process the request. You will receive a notice in My Notices when the status changes. If you see no update after two business days, contact your handler via Messages.

**4. UNDERSTANDING COVER STATUSES**

Pending Cover — The vehicle has been submitted and is awaiting review and approval from your Myshield handler. The vehicle is not yet insured.

On Cover — The vehicle has been approved and has active, confirmed insurance cover from the cover effective date set by your handler.

Awaiting Inception — The vehicle has been approved and cover is confirmed, but the cover start date is set in the future. The vehicle will automatically move to On Cover when the cover start date arrives. You will see a pulsing purple Awaiting Inception label in the Vehicles list until that date.

Inactive — The vehicle has been removed from your policy. It is no longer insured. Inactive vehicles remain visible in your Vehicles list for record-keeping.

**5. VIEWING VEHICLE DETAILS AND HISTORY**

Click any vehicle in the list to open its full record. You can see all vehicle details, the complete cover history (every status change with date and who processed it), and any notes from your handler. This history is a permanent audit record — keep it for reference if you need to resolve a query with your handler or insurer.

**6. MANAGING ONS AND OFFS**

The cleanest way to manage vehicles coming on and off cover is to **add** and **remove** them directly. When a vehicle needs cover, use **Add Perm Vehicle** or **Add Temp Vehicle**. When cover is no longer required, use **Remove Vehicle** and set the correct cover end date. Keep your live fleet accurate by adding and removing as needed rather than leaving unused vehicles on the policy.

**7. WHAT TO DO IF A VEHICLE SHOWS AN UNEXPECTED STATUS**

Do not attempt to change the status yourself. Contact your Myshield handler via Messages. Provide the registration, the status currently showing, and what you believe it should be. Your handler will investigate and correct it if needed. If a vehicle remains Pending Cover for more than two business days with no notice, chase your handler directly.

**8. DECLARED VALUES — IMPORTANT**

Always use current market values. If a vehicle's value changes significantly — after a major repair, modification, or shift in market prices — contact your handler to update the record. Keeping declared values accurate protects you in the event of a claim.`,
      readingContent: `Managing your fleet vehicles is done from the **Vehicles** page in the My Fleet section of the sidebar. To add a new vehicle, click the **Add Vehicle** button, enter the vehicle's registration number, then use the **DVLA lookup** to automatically fill in the make, model, year, and vehicle type. You will also need to set the vehicle's declared value.

Once submitted, your new vehicle submission goes to your **Myshield handler** for review and approval. You will receive a notice in **My Notices** when the status of your vehicle changes.

Every vehicle on your fleet has one of four **cover statuses**: **Pending Cover** means the vehicle has been submitted and is awaiting approval from your handler. **On Cover** means the vehicle is active and has confirmed insurance cover. **Awaiting Inception** means the vehicle has been approved but the cover start date is set in the future — it will move to On Cover automatically when that date arrives and appears with a pulsing purple label in your list. **Inactive** means the vehicle has been removed from your policy.

The cleanest way to manage ons and offs is to **add** and **remove** vehicles directly. Add a vehicle when cover is needed, and remove it when cover is no longer required. Understanding cover statuses helps you keep track of exactly which vehicles are protected at any given time. If you notice a vehicle showing an unexpected status, contact your Myshield handler to discuss it. You can view full details for each vehicle by clicking on it from the Vehicles page.`,
      questions: [
        {
          question: "What does 'Pending Cover' mean for a vehicle?",
          options: [
            'The vehicle is fully insured',
            'The vehicle has been submitted and is awaiting approval',
            'The vehicle has been removed from the policy',
            'The vehicle needs renewing',
          ],
          correct: 1,
          explanation: 'Pending Cover means the vehicle has been submitted to your Myshield handler and is waiting for their review and approval.',
        },
        {
          question: 'Where do you add a new vehicle?',
          options: [
            'Dashboard',
            'Messages',
            'Vehicles page → Add Vehicle',
            'Sites',
          ],
          correct: 2,
          explanation: 'New vehicles are added from the Vehicles page using the Add Vehicle button.',
        },
        {
          question: 'What does the DVLA lookup do when adding a vehicle?',
          options: [
            "Checks your driver's licence",
            'Auto-fills vehicle details from the registration number',
            'Sends an email to your insurer',
            'Approves the vehicle instantly',
          ],
          correct: 1,
          explanation: 'The DVLA lookup uses the registration number to automatically populate the make, model, year, and vehicle type fields.',
        },
        {
          question: 'A vehicle is approved but the cover start date is set in the future. What status does it show?',
          options: [
            'On Cover',
            'Cover Pending',
            'Awaiting Inception',
            'Inactive',
          ],
          correct: 2,
          explanation: "Awaiting Inception means the vehicle has been approved and cover is confirmed, but the start date is in the future. It will move automatically to On Cover when the cover start date arrives. It shows as a pulsing purple label in your Vehicles list.",
        },
        {
          question: 'If your vehicle does not qualify for instant cover, who will manage the approval process for you?',
          options: [
            'The DVLA',
            'Your site manager',
            'Your Myshield handler',
            'It is approved automatically',
          ],
          correct: 2,
          explanation: 'If a vehicle does not meet the criteria for instant cover, your Myshield handler reviews and approves the submission for you.',
        },
      ],
    },
    {
      index: 2,
      title: 'Making Requests',
      manualContent: `**FULL TRAINING GUIDE — Making Requests (Mid-Term Adjustments)**

This guide explains each of the four request types available to you, with exact steps, what happens at each stage, and how to handle a declined request.

**WHAT IS A MID-TERM ADJUSTMENT?**

A mid-term adjustment (MTA) is any change made to your fleet policy between renewal dates. All requests are reviewed by your Myshield handler. You will receive a notice in My Notices when each request is processed.

**REQUEST TYPE 1 — REMOVE A VEHICLE**

Use this when a vehicle is being taken off your fleet and you need cover to end.

Step 1 — Go to Vehicles in My Fleet.
Step 2 — Find the vehicle and open its actions menu.
Step 3 — Select Request Removal.
Step 4 — Enter the cover end date (the date you want cover to stop).
Step 5 — Click Submit. The request goes to your handler for approval.
Step 6 — Once approved you will receive a notice and the vehicle status will change to Inactive.

**REQUEST TYPE 2 — REINSTATEMENT**

Use this when a vehicle is currently Inactive and you want it back on cover.

Step 1 — Go to Vehicles and locate the Inactive vehicle.
Step 2 — Open the actions menu and select Request Reinstatement.
Step 3 — Submit the request. Your handler will review it and set a cover restart date on approval.
Step 4 — Once approved the vehicle returns to On Cover status and you receive a notice.

**REQUEST TYPE 3 — TEMPORARY COVER**

Use this for a vehicle that needs cover for a defined short period, such as a loaned or seasonal vehicle. The maximum duration is 30 days.

Step 1 — Go to Vehicles and open the actions menu for the relevant vehicle, or use Add Vehicle for a new temporary vehicle.
Step 2 — Select Temporary Cover and enter the start date and end date. The system will reject dates spanning more than 30 days.
Step 3 — Submit the request. Your handler reviews and approves the specified dates.
Step 4 — Once approved a notice is generated confirming the temporary cover period.

**REQUEST TYPE 4 — CHANGE OF VEHICLE**

Use this when you need to replace one vehicle registration with another — for example a replacement vehicle.

Step 1 — Go to Vehicles and open the actions menu for the vehicle being replaced.
Step 2 — Select Change of Vehicle.
Step 3 — Enter the new vehicle registration and use the DVLA lookup to fill in details.
Step 4 — Submit the request. Your handler processes the removal of the old registration and the addition of the new one simultaneously.
Step 5 — Once approved both changes appear in your vehicle list and notices are generated.

**TRACKING YOUR REQUESTS**

After submitting any request, check My Notices regularly for updates. Each notice shows the action taken, the date processed, and any notes from your handler. You do not need to call or email — all communication is handled through the portal.

**IF YOUR REQUEST IS DECLINED**

You will receive a decline notice explaining the reason. Read the reason carefully. Contact your Myshield handler via Messages to discuss the issue and understand what needs to change before resubmitting. Do not resubmit the same request without first resolving the issue identified in the decline reason.`,
      readingContent: `During the life of your policy, you may need to make changes to the vehicles on your fleet. These are called **mid-term adjustments** and there are four types you can request directly from the portal.

**(1) Remove a Vehicle** — If you need to take a vehicle off cover, go to the Vehicles page, open the vehicle's actions menu, select **Request Removal**, and enter the cover end date.

**(2) Reinstatement** — If a vehicle is currently Inactive and you want it back on cover, you can submit a **Reinstatement** request from the vehicle's actions menu.

**(3) Temporary Cover** — You can request cover for a specific set of dates, up to a **maximum of 30 days**. This is useful for loaned or seasonal vehicles.

**(4) Change of Vehicle** — If you need to swap one vehicle registration for another (for example, a replacement vehicle), use a Change of Vehicle request.

All requests go to your **Myshield handler** for review and approval. You will receive a notice in My Notices when your request has been processed. You do not need to call or email — everything is handled through the portal.`,
      questions: [
        {
          question: 'What is the maximum duration for a temporary cover request?',
          options: ['14 days', '60 days', '30 days', '7 days'],
          correct: 2,
          explanation: 'Temporary cover requests are limited to a maximum of 30 days. They are useful for loaned or seasonal vehicles.',
        },
        {
          question: 'How do you add a permanent vehicle to your fleet in the portal?',
          options: [
            'Send an email to your Myshield handler',
            'Go to the Vehicles page, click Add Perm Vehicle, enter the details and submit',
            'Call the Myshield team on 0333 323 9660',
            'It is added automatically when purchased',
          ],
          correct: 1,
          explanation: 'To add a permanent vehicle, go to the Vehicles page and click Add Perm Vehicle. Enter the registration, use the DVLA lookup to fill in the details, and submit for approval.',
        },
        {
          question: 'Where do you find the option to request a vehicle removal?',
          options: [
            'Dashboard',
            "The vehicle's actions menu on the Vehicles page",
            'Messages',
            'Sites',
          ],
          correct: 1,
          explanation: "To request a removal, go to the Vehicles page, open the vehicle's actions menu, and select Request Removal.",
        },
        {
          question: 'How do you move a vehicle from one site to another within your fleet?',
          options: [
            'Remove the vehicle and re-add it at the new site',
            'Contact your Myshield handler by phone',
            'Use the Change of Site option in the vehicle actions menu on the Vehicles page',
            'It updates automatically when you change the site address',
          ],
          correct: 2,
          explanation: 'To move a vehicle between sites, open the vehicle actions menu on the Vehicles page and select Change of Site. Your handler processes this immediately.',
        },
        {
          question: 'What happens after you submit a mid-term adjustment request?',
          options: [
            'It is automatically approved immediately',
            'Your Myshield handler reviews and approves it',
            'It is rejected immediately',
            'Nothing happens until you call',
          ],
          correct: 1,
          explanation: 'All mid-term adjustment requests are reviewed by your Myshield handler, who will process them and notify you via My Notices.',
        },
      ],
    },
    {
      index: 3,
      title: 'Notices & Messages',
      manualContent: `**FULL TRAINING GUIDE — Notices and Messages**

This guide explains how all cover decisions and policy updates are communicated to you through the My Feed section, and how to manage them effectively.

**1. MY FEED — WHAT IT CONTAINS**

My Feed in the sidebar contains two sections: My Notices and Messages. My Notices is where all decisions about your fleet are communicated. Messages is where you can correspond directly with your Myshield handler. Check My Feed every time you log in.

**2. TYPES OF NOTICES**

Approval Notice — Issued when a vehicle submission or request has been approved by your handler. Contains vehicle details, the action taken, the cover effective date, who processed it, and the date. A To Whom It May Concern notification confirming the adjustment is also generated and held on record.

Decline Notice — Issued when a request has been declined. Contains the reason for the decline. Read the reason carefully and contact your handler via Messages if you need to discuss it before resubmitting.

Status Change Notice — Issued whenever a vehicle's cover status changes. Provides a full summary of what changed and when.

Information Notice — General updates or messages from your handler about your policy or fleet.

**3. WHAT TO DO WITH AN APPROVAL NOTICE**

Step 1 — Open the notice from My Notices.
Step 2 — Review the details: vehicle registration, action taken, cover effective date, and processing notes.
Step 3 — Confirm the details are correct. If anything looks wrong (wrong registration, incorrect date) contact your handler immediately via Messages.
Step 4 — Mark the notice as read once reviewed. This keeps your feed organised and makes it easy to see which items still need attention.

**4. WHAT TO DO WITH A DECLINE NOTICE**

Step 1 — Open the decline notice and read the reason carefully.
Step 2 — Do not resubmit the same request without addressing the decline reason.
Step 3 — If you do not understand the reason or disagree with the decision, contact your handler via Messages. Include the vehicle registration and the decline notice date in your message.
Step 4 — Once the issue is resolved, resubmit the corrected request.

**5. MARKING NOTICES AS READ**

Use the Mark as Read button on each notice once you have reviewed it. Unread notices are highlighted so you can quickly see what needs attention. Do not leave large numbers of unread notices — review and mark them regularly to maintain a clean, manageable feed.

**6. USING MESSAGES**

The Messages section allows you to send and receive messages directly with your Myshield handler. Use Messages for: querying a decision, chasing a pending request, providing additional information requested by your handler, or raising a general question about your policy. Always include the vehicle registration number when referring to a specific vehicle request.

**7. THE AUDIT TRAIL**

Every notice contains a complete record of the action: vehicle details, who processed it, the date, and any notes. This forms a permanent audit trail of all changes to your policy. Keep notices for your records — they serve as confirmation of each mid-term adjustment made to your fleet.`,
      readingContent: `All decisions about your fleet are communicated through **My Notices**, found in the **My Feed** section of the sidebar. This is the most important place to check for updates on your vehicle submissions and requests.

When a vehicle is approved, you will receive an **Approved notice** containing all the relevant details, and a **To Whom It May Concern notification** will be attached. This notification serves as formal confirmation of the mid-term adjustment and can be saved for your records. When a request is declined, you will receive a notice explaining the reason.

Each notice shows the **vehicle details**, the action taken, who processed the request, and the date it was processed. This gives you a clear audit trail of all changes to your policy.

To keep your feed organised, **mark notices as read** once you have reviewed them. This makes it easy to see which updates still need your attention. If a vehicle request has been declined, review the reason in the notice and contact your **Myshield handler** to discuss how to resolve it. Your handler is your first point of contact for any questions about a decision.`,
      questions: [
        {
          question: 'Where do you find cover decision notices?',
          options: ['Dashboard', 'My Notices in My Feed', 'Vehicles page', 'Reports'],
          correct: 1,
          explanation: 'All cover decisions and policy updates are communicated in My Notices, found in the My Feed section.',
        },
        {
          question: 'What information is included in an approval notice?',
          options: [
            'Just the vehicle registration',
            'Vehicle details, action taken, who processed it, and the date',
            'Only the cover letter',
            'Your policy number only',
          ],
          correct: 1,
          explanation: 'Approval notices include the full vehicle details, the action taken, who processed it, and the date — giving you a clear audit record.',
        },
        {
          question: 'What is attached to an approval notice?',
          options: [
            'An invoice',
            'A To Whom It May Concern notification confirming the adjustment',
            'Your full policy schedule',
            'A tax document',
          ],
          correct: 1,
          explanation: 'A To Whom It May Concern notification is automatically generated and attached to approval notices as formal confirmation of the mid-term adjustment.',
        },
        {
          question: 'Why should you mark notices as read?',
          options: [
            'To permanently delete them',
            'To keep your My Notices feed organised',
            'To approve the vehicle yourself',
            'To generate a report',
          ],
          correct: 1,
          explanation: 'Marking notices as read keeps your My Notices feed tidy and makes it easy to see which updates still need your attention.',
        },
        {
          question: 'What should you do if your vehicle request is declined?',
          options: [
            'Re-submit immediately with no changes',
            'Contact your Myshield handler to discuss and resolve it',
            'Ignore it',
            'Remove the vehicle yourself',
          ],
          correct: 1,
          explanation: 'Contact your Myshield handler — the decline notice will explain the reason and your handler can help you resolve it.',
        },
      ],
    },
    {
      index: 4,
      title: 'Data & Privacy',
      manualContent: `**FULL TRAINING GUIDE — Data and Privacy**

This guide explains how your data is collected and used, what your rights are under GDPR, and how to raise concerns about data handling.

**1. WHAT DATA IS COLLECTED**

Myshield collects the following categories of data to manage your fleet insurance policy: login records (username, IP address, timestamp); fleet vehicle data (registrations, values, cover statuses, history); named driver data (name, date of birth, licence number, licence category, licence images); consent records (GDPR acceptance history, Academy completion records); and communication records (notices, messages, workstream actions).

This data is collected on behalf of Yourshield Ltd, the FCA-authorised broker and data controller. The legal basis for processing is the performance of an insurance contract and compliance with legal obligations.

**2. HOW YOUR DATA IS USED**

Your fleet data is used to: manage your insurance policy and process mid-term adjustments; generate To Whom It May Concern notifications confirming policy changes; meet FCA regulatory obligations including Treating Customers Fairly; and maintain audit records for compliance purposes.

Your data is not sold to third parties. It may be shared with Yourshield Ltd and regulatory bodies (the FCA) where required by law or by the terms of your policy.

**3. YOUR GDPR RIGHTS**

Under GDPR you have the following rights: Right of Access — you may request a copy of the personal data held about you. Right to Rectification — you may request correction of inaccurate data. Right to Erasure — you may request deletion of your data in certain circumstances. Right to Restriction — you may request that processing of your data is limited. Right to Object — you may object to certain types of processing.

To exercise any of these rights, contact Yourshield Ltd at yourshield.co.uk. Response times are within 30 days as required by GDPR.

**4. CONSENT RECORDS**

You can view your full consent history at any time in Consent Records, found in Admin Toolkit in the sidebar. This shows every GDPR consent event: your data privacy acceptance on first login, any subsequent consent updates, driver licence upload consents, and Academy training completion records. These records are permanent and cannot be altered.

**5. NAMED DRIVER DATA — YOUR OBLIGATIONS**

Named driver data — including date of birth, licence number, and licence images — is sensitive personal data under GDPR. You must only share details of drivers who are authorised on your fleet policy. Do not upload data for individuals who are not on your policy. Obtain the driver's consent before uploading their personal details and licence images. Store any local copies of driver data securely and delete them when no longer needed.

**6. RAISING A DATA CONCERN OR COMPLAINT**

If you believe your data has been handled incorrectly: Step 1 — Contact Yourshield Ltd at yourshield.co.uk to raise your concern directly. Step 2 — If you are not satisfied with the response, you may escalate to the Information Commissioner's Office (ICO) at ico.org.uk. Step 3 — To raise a general complaint about Yourshield's services, visit yourshield.co.uk/complaints.

**7. DATA RETENTION**

Your data is retained for the duration of your policy plus a period required by FCA regulations and insurance law (typically 6 years after policy termination). After this period data is securely deleted or anonymised in accordance with Yourshield's data retention policy.

**KEY CONTACTS**

Yourshield Ltd: yourshield.co.uk. Privacy Policy: yourshield.co.uk/privacy-policy. Complaints: yourshield.co.uk/complaints. FCA Firm Reference Number: 617848.`,
      readingContent: `Myshield collects your fleet data, login records, and consent information as part of managing your insurance policy. This is done in compliance with **GDPR** (the General Data Protection Regulation), which governs how personal data is collected, used, and stored.

You can view your **consent history** at any time in **Consent Records**, found in the Admin Toolkit section of the sidebar. You accepted the data privacy notice when you **first logged in** to the portal.

When it comes to driver data, only share personal details (such as licence copies) for **authorised drivers on your fleet policy**. Named driver data — including date of birth, licence number, and licence category — is sensitive personal data and is handled under GDPR. Do not share personal information about individuals who are not authorised drivers on your policy.

If you have questions about how your data is used, contact **Yourshield Ltd** at **yourshield.co.uk**. To raise a complaint about data handling, visit **yourshield.co.uk/complaints**. The full privacy policy is available at **yourshield.co.uk/privacy-policy**. Yourshield is the FCA-authorised broker and data controller.`,
      questions: [
        {
          question: 'Where can you view your consent history?',
          options: ['Dashboard', 'Messages', 'Consent Records in Admin Toolkit', 'Sites'],
          correct: 2,
          explanation: 'Consent Records in the Admin Toolkit section shows a full history of your GDPR consent events.',
        },
        {
          question: 'When did you accept the data privacy notice?',
          options: [
            'When you added your first vehicle',
            'When you first logged in to the portal',
            'When you completed this training',
            'When requested by your insurer',
          ],
          correct: 1,
          explanation: 'You accepted the data privacy notice when you first logged in to the Myshield Fleet Portal.',
        },
        {
          question: 'What regulation covers the handling of driver personal data?',
          options: ['The Highway Code', 'GDPR', 'The Insurance Act', 'FCA Rules'],
          correct: 1,
          explanation: 'GDPR (General Data Protection Regulation) governs the collection, use, and storage of personal data including driver information.',
        },
        {
          question: "Where can you find Yourshield's complaints procedure?",
          options: [
            'yourshield.co.uk/complaints',
            'yourshield.co.uk/vehicles',
            'The Vehicles page',
            'Admin Tools',
          ],
          correct: 0,
          explanation: "Yourshield's complaints procedure is available at yourshield.co.uk/complaints.",
        },
        {
          question: 'Which drivers should be submitted through the Referred Named Drivers section?',
          options: [
            'All employees at the company',
            'Only drivers who fall outside your policy\'s standard driving warranty requirements',
            'Every person who has ever driven a company vehicle',
            'Only drivers with a clean driving licence',
          ],
          correct: 1,
          explanation: 'The Referred Named Drivers section is only for drivers who fall outside your policy\'s standard driving warranty. Drivers who meet the standard requirements do not need to be listed here.',
        },
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// TRACK 1 — Fleet Operations (FleetManager role)
// ─────────────────────────────────────────────────────────────────────────────
const fleetManagerTrack: AcademyTrack = {
  trackName: 'Fleet Operations',
  role: 'FleetManager',
  modules: [
    {
      index: 0,
      title: 'Portal Overview',
      manualContent: `**FULL TRAINING GUIDE — Portal Overview for Fleet Managers**

This guide covers every section of the portal available to you, your daily workflow, and your core responsibilities.

**1. YOUR PORTAL SECTIONS**

As a Fleet Manager you have access to: Dashboard (live fleet overview and pending actions); Workstream (your primary approval queue); Clients (all client records, cover settings, and vehicle lists); Vehicles (full fleet vehicle management across all clients); Named Drivers (driver records, licence management, and expiry monitoring); Sites (client operating locations); Messages (direct communication); Insights & Reports (fleet reporting and data export); and Academy (your training programme).

**2. THE DASHBOARD — YOUR DAILY STARTING POINT**

Log in and check the Dashboard every morning before doing any other work. The Dashboard shows: total vehicle count across all clients; On Cover, Pending Cover, and Inactive counts; items requiring immediate attention (pending workstream submissions, expiring driver licences). If any urgent items are flagged, address them first before moving to other tasks.

**3. THE WORKSTREAM — YOUR PRIMARY RESPONSIBILITY**

The Workstream is the core of your daily operations. Every time a client submits a vehicle addition, removal, reinstatement, temporary cover request, or change of vehicle, it appears here. Process workstream items promptly — clients are waiting for decisions on their fleet cover. Aim to process all items within one business day.

**4. MANAGING MULTIPLE CLIENTS**

You may manage multiple client accounts. Each client has their own record in the Clients section. Before processing a workstream item, review the relevant client's record to understand their policy context: check their Cover Settings (instant cover enabled?), their Named Driver Policy, and their insurer midterm email address. Processing without this context can lead to errors.

**5. NAMED DRIVERS — ONGOING MONITORING**

Named driver compliance is an ongoing responsibility. Use the Expiring Soon filter in the Named Drivers section to identify licences approaching expiry. Contact the client to obtain the renewed licence and upload the new images before the expiry date. Letting licences expire without updating the record creates a compliance gap.

**6. AUDIT ACCOUNTABILITY**

All your actions — approvals, rejections, notes, driver record changes — are permanently recorded in the audit log with your username and a timestamp. This means every action is traceable. Always add clear notes when processing workstream items; vague or missing notes undermine the compliance record and can cause issues during FCA reviews.

**YOUR DAILY WORKFLOW — RECOMMENDED ORDER**

Step 1 — Check the Dashboard for urgent items. Step 2 — Process all Workstream items. Step 3 — Review Named Drivers for expiring licences. Step 4 — Check Messages for client queries. Step 5 — Run any required reports. Step 6 — Log out properly when finished.`,
      readingContent: `As a **Fleet Manager**, you have access to a comprehensive set of tools to manage your clients' fleets. The main sections available to you are: **Workstream** (your approval queue), **Vehicles** (all fleet vehicles across your assigned clients), **Named Drivers**, **Sites**, **Messages**, and **Insights & Reports**.

The **Dashboard** provides a live overview of fleet statistics and pending actions. It shows cover status counts across all your vehicles and highlights items that require your attention, such as pending workstream items or expiring driver licences.

Your **primary responsibility** is managing the Workstream — reviewing and processing client vehicle submissions. Every time a client adds a vehicle, requests a removal, reinstatement, temporary cover, or a change of vehicle, it appears in your Workstream for approval.

Beyond the Workstream, you can manage vehicles directly on behalf of clients, run fleet reports, monitor named driver compliance, and manage sites. Use the **Named Drivers** section to monitor upcoming licence expiry dates — the Expiring Soon filter helps you stay ahead of compliance obligations. All your actions are recorded in the system audit log.`,
      questions: [
        {
          question: 'What is the primary role of a Fleet Manager in Myshield?',
          options: [
            'Managing client billing',
            'Processing workstream actions and managing fleet vehicles',
            'Creating client company accounts',
            'Setting insurer premium rates',
          ],
          correct: 1,
          explanation: "A Fleet Manager's primary role is processing workstream actions (approving/rejecting vehicle requests) and managing fleet vehicles on behalf of clients.",
        },
        {
          question: 'Where do you see all pending client requests?',
          options: ['Dashboard', 'Messages', 'Workstream', 'Reports'],
          correct: 2,
          explanation: 'The Workstream shows all pending items from clients, including vehicle additions, removals, reinstatements, temp cover, and change-of-vehicle requests.',
        },
        {
          question: 'What does the Dashboard show a Fleet Manager?',
          options: [
            'Only your personal vehicle list',
            'Live fleet stats, cover status counts and pending actions',
            'Insurance policy terms and conditions',
            'Driver licence expiry dates only',
          ],
          correct: 1,
          explanation: 'The Dashboard shows live fleet statistics, cover status counts across all vehicles, and highlights pending actions that need attention.',
        },
        {
          question: 'Which of these is NOT a Fleet Manager responsibility?',
          options: [
            'Approving vehicle additions',
            'Managing named drivers',
            'Setting client insurance premium rates',
            'Running fleet reports',
          ],
          correct: 2,
          explanation: 'Setting client insurance premium rates is handled by Yourshield, not by Fleet Managers. Fleet Managers manage vehicles, drivers, workstream items, and reports.',
        },
        {
          question: 'Where do you find expiring driver licences?',
          options: [
            'Dashboard',
            'Named Drivers → Expiring Soon filter',
            'Workstream',
            'Admin Tools',
          ],
          correct: 1,
          explanation: 'The Named Drivers section has an Expiring Soon filter that highlights licences approaching their expiry date.',
        },
      ],
    },
    {
      index: 1,
      title: 'Vehicle & Fleet Management',
      manualContent: `**FULL TRAINING GUIDE — Vehicle and Fleet Management**

This guide covers adding single vehicles, bulk uploads, understanding cover statuses, and managing vehicle records.

**1. ADDING A SINGLE VEHICLE**

Step 1 — Go to the Vehicles page and click Add Vehicle.
Step 2 — Enter the registration number. The system normalises formatting automatically.
Step 3 — Click DVLA Lookup. Verify that the Make, Model, Year, and Vehicle Type returned are correct. If the vehicle is new or has a personalised plate the lookup may return no result — fill in fields manually.
Step 4 — Select the correct Site if the client has multiple operating locations.
Step 5 — Enter the Weight Class (if required by the client's policy).
Step 6 — Enter the Declared Value. Use current market value. This is used by the insurer for rating.
Step 7 — Click Submit. The vehicle enters Pending Cover status and appears in the Workstream for processing.

**2. BULK CSV UPLOAD — STEP BY STEP**

Step 1 — Go to the Vehicles page and click Bulk Upload.
Step 2 — Click Download Template to get the CSV template file.
Step 3 — Open the template in Excel or a spreadsheet application.
Step 4 — Fill in one row per vehicle. Required columns: Registration, Make, Model, Type, Weight Class, Year, Value, Site.
Step 5 — Save the file as CSV (not .xlsx).
Step 6 — Return to the portal, click Choose File, select your CSV, and click Upload.
Step 7 — The system processes each row. Any errors (missing fields, duplicate registrations) are flagged. Fix errors and re-upload.
Step 8 — On success, all vehicles are created in Pending Cover status and a batch ID is assigned.

If you made an error in the upload: go to Admin Tools, find the batch ID, and click Undo Batch. This removes all vehicles from that upload. You can then correct the CSV and re-upload.

**3. COVER STATUS LIFECYCLE**

Pending Cover — Submitted, awaiting approval. Not yet insured. The vehicle appears in the Workstream.
On Cover — Approved and actively insured from the cover effective date.
Inactive — Removed from the policy. No longer insured.

If a client has Instant Cover enabled in their Cover Settings, new submissions skip Pending Cover and go directly to On Cover without workstream approval.

**4. MANAGING ONS AND OFFS — ADD AND REMOVE DIRECTLY**

The cleanest way to manage vehicles coming on and off cover is to **add** and **remove** them directly. When a vehicle needs cover, submit an addition. When cover is no longer required, submit a removal request with the correct cover end date. Do not leave vehicles sitting unused on the policy — keep the live fleet accurate by adding and removing as needed.

To view the complete history of any vehicle — all status changes, dates, and processing notes — click on the vehicle to open its record and select the History tab.

**5. HANDLING INCORRECT VEHICLE DATA**

If you discover a vehicle has incorrect data (wrong make/model, wrong value): contact the client to confirm correct details, then edit the vehicle record. If the value needs updating, note the change in the vehicle record and ensure the insurer is notified via normal MTA processes. Do not leave incorrect data uncorrected — it affects policy validity.`,
      readingContent: `You can add vehicles to a client's fleet in two ways. To add a single vehicle, go to the **Vehicles** page and click **Add Vehicle**. Enter the registration and use the **DVLA lookup** to auto-fill the make, model, year, and type. You will also need to set the vehicle's site, weight class, and declared value.

For larger additions, use the **bulk CSV upload**. Download the template from the Vehicles page, fill in the required columns (Registration, Make, Model, Type, Weight Class, Year, Value, Site), and upload the completed file. If you make a mistake, you can **undo a bulk upload batch** from Admin Tools using the batch ID.

Cover follows the standard progression: **Pending Cover → On Cover → Inactive**. If a client has **Instant Cover** enabled, newly submitted vehicles bypass the pending stage and go directly to **On Cover** without requiring manual approval. You can check and configure this in the client's Cover Settings.

The cleanest way to manage ons and offs is to **add** and **remove** vehicles directly. When cover is needed, add the vehicle. When cover is no longer required, remove it with the correct end date. You can also view the full **vehicle history** to see all status changes, notes, and approval records — useful for resolving queries and for audit purposes.`,
      questions: [
        {
          question: 'How do you bulk upload vehicles to a client fleet?',
          options: [
            'Add them one by one from the Vehicles page',
            'Download the CSV template, fill it in, and upload it',
            'Email the registration list to Yourshield',
            'Import directly from the DVLA',
          ],
          correct: 1,
          explanation: 'Bulk uploads use a CSV template downloaded from the Vehicles page. Fill in all required columns, then upload the completed file.',
        },
        {
          question: 'What does enabling Instant Cover do for a client?',
          options: [
            'Skips the DVLA lookup step',
            'Puts new vehicles directly On Cover without manual approval',
            'Automatically approves all driver additions',
            'Sends immediate emails to the insurer for each submission',
          ],
          correct: 1,
          explanation: 'Instant Cover means that when a client submits a vehicle, it goes directly to On Cover status without needing to go through the Workstream.',
        },
        {
          question: 'Where can you undo a bulk vehicle upload?',
          options: ['Vehicles page', 'Workstream', 'Admin Tools', 'Reports'],
          correct: 2,
          explanation: 'Bulk upload batches can be reversed from Admin Tools using the batch ID. This undoes all vehicle additions in that batch.',
        },
        {
          question: 'What is the correct cover status progression for a vehicle?',
          options: [
            'On Cover → Pending Cover → Inactive',
            'Inactive → On Cover → Pending Cover',
            'Pending Cover → On Cover → Inactive',
            'Active → Pending → Removed',
          ],
          correct: 2,
          explanation: 'Vehicles progress from Pending Cover (submitted, awaiting approval) → On Cover (active) → Inactive (removed from policy).',
        },
        {
          question: 'What information is required in the CSV upload template?',
          options: [
            'Only Registration and Make',
            'Registration, Make, Model, Type, Weight Class, Year, Value',
            'Just registration and declared value',
            'Only the Registration number',
          ],
          correct: 1,
          explanation: 'The CSV template requires: Registration, Make, Model, Type, Weight Class, Year, Value, and Site for each vehicle.',
        },
      ],
    },
    {
      index: 2,
      title: 'Workstream Processing',
      manualContent: `**FULL TRAINING GUIDE — Workstream Processing**

This guide walks through every workstream item type and how to process each one correctly, including what to do with unusual or complex requests.

**1. THE FIVE WORKSTREAM ITEM TYPES**

Vehicle Addition — Client has submitted a new vehicle for cover. Review registration, make/model, vehicle type, weight class, and declared value before approving.
Removal — Client wants to take a vehicle off cover. Set a cover end date on approval.
Reinstatement — Client wants an Inactive vehicle restored to cover. Set a cover restart date.
Temporary Cover — Client wants cover for defined dates (maximum 30 days). Confirm start and end dates.
Change of Vehicle — Client swapping one registration for another. Old vehicle is removed, new vehicle is added simultaneously.

**2. APPROVING A WORKSTREAM ITEM — STEP BY STEP**

Step 1 — Open the workstream item and review all details carefully. Check vehicle data against the client record. If anything looks wrong, contact the client via Messages before processing.
Step 2 — Set the Cover Effective Date. This is the date from which the change takes effect. For additions this is when cover begins; for removals it is when cover ends. Use today's date unless there is a specific reason to use a different date. Admins may backdate with justification; non-admin roles cannot backdate.
Step 3 — Add clear Processing Notes describing the action taken. These notes appear in the client's notification and in the audit record. Be specific: include vehicle registration, action, and any relevant context.
Step 4 — Click Approve. The system automatically generates a To Whom It May Concern notification and emails it to both the client and the insurer's midterm email address. The workstream item is resolved and the vehicle status updates.

**3. REJECTING A WORKSTREAM ITEM**

Step 1 — Open the item and click Reject.
Step 2 — Enter a clear Decline Reason. This appears in the client's decline notice — it must be specific and actionable so the client understands what to change.
Step 3 — Tick the confirmation that the customer was contacted (or will be contacted). This is required under FCA Treating Customers Fairly obligations.
Step 4 — Click Confirm. The client receives a decline notice with your reason.

**4. BULK PROCESSING**

Use Bulk Approve or Bulk Reject when a client submits many vehicles at once. Select all items you want to process together, set a single cover effective date that applies to all, add bulk notes, and confirm. Individual items can be excluded from the bulk action if they need separate handling.

**5. IF THE INSURER MIDTERM EMAIL IS MISSING OR WRONG**

Before approving, check that the client's insurer midterm email is set correctly in their Client Details. If it is missing or incorrect, the insurer will not receive the notification. Update the email address in the client record first, then process the workstream item. Log this as a note on the workstream item.

**6. PENDING ITEMS CHECK AT LOGOUT**

When you log out, the portal checks for outstanding workstream items. If any are pending you will see a warning. Do not routinely dismiss this warning without reviewing the outstanding items — a missed approval means a client vehicle may remain uninsured overnight.`,
      readingContent: `The **Workstream** is the core of your daily work. It shows all pending items from clients: vehicle **additions**, **removals**, **reinstatements**, **temporary cover** requests, and **change-of-vehicle** requests.

For each item you have three options. **Approve** — set a cover effective date (the date from which the change takes effect) and add processing notes. When you approve, a **To Whom It May Concern notification** is automatically generated and emailed to both the client and the insurer's midterm address. **Reject** — enter a decline reason and confirm that the customer was contacted. The client will receive a decline notice with the reason. **Bulk Approve or Reject** — select multiple items and process them together, which is useful when a client has submitted many vehicles at once.

Always add **clear and accurate notes** when processing items — these notes appear in the client's notification and form part of the audit trail. When approving, always set the correct **cover effective date** — this determines when the insurance change takes effect.

For rejections, it is important to record that **the customer was contacted** before or after the decision. This supports your Treating Customers Fairly obligations under FCA guidelines.`,
      questions: [
        {
          question: 'What happens automatically when you approve a vehicle?',
          options: [
            'Nothing additional',
            'A To Whom It May Concern notification is generated and emailed to the client and insurer',
            'The vehicle is added to your personal list',
            'An invoice is created',
          ],
          correct: 1,
          explanation: 'On approval, the system automatically generates a To Whom It May Concern notification and emails it to the client and the insurer\'s midterm address.',
        },
        {
          question: 'What must you record when rejecting a workstream request?',
          options: [
            'Nothing extra is required',
            'Decline reason and confirmation that the customer was contacted',
            'Only the vehicle registration number',
            'A new proposed cover date',
          ],
          correct: 1,
          explanation: 'When rejecting, you must record the decline reason and confirm the customer was contacted. This supports Treating Customers Fairly obligations.',
        },
        {
          question: 'Which of these is NOT a type of workstream item?',
          options: [
            'Vehicle addition',
            'Temporary cover request',
            'Driver licence renewal',
            'Change of vehicle',
          ],
          correct: 2,
          explanation: 'Driver licence renewals are managed in the Named Drivers section, not the Workstream. The five workstream types are: addition, removal, reinstatement, temp cover, and change of vehicle.',
        },
        {
          question: 'When should you use the Bulk Approve function?',
          options: [
            'Only for single vehicles',
            'When approving multiple vehicles at once',
            'Only on the first day of the month',
            'Only when a client has Instant Cover enabled',
          ],
          correct: 1,
          explanation: 'Bulk Approve allows you to select and approve multiple workstream items at the same time, saving time when a client submits several vehicles.',
        },
        {
          question: 'What should you always set when approving a workstream item?',
          options: [
            'Nothing extra is needed beyond clicking Approve',
            'A cover effective date and any relevant processing notes',
            'Only the vehicle declared value',
            "The client's phone number",
          ],
          correct: 1,
          explanation: 'Always set a cover effective date (when the change takes effect) and add clear notes. Both appear in the notification and in the client\'s audit record.',
        },
      ],
    },
    {
      index: 3,
      title: 'Driver Administration',
      manualContent: `**FULL TRAINING GUIDE — Driver Administration**

This guide covers adding named drivers, uploading licence images, applying the Named Driver Policy, and monitoring licence expiry.

**1. ADDING A NEW NAMED DRIVER — STEP BY STEP**

Step 1 — Go to Named Drivers in the sidebar.
Step 2 — Click Add Driver.
Step 3 — Enter the driver's full legal name exactly as it appears on their driving licence.
Step 4 — Enter date of birth. This is used to verify minimum age eligibility under the Named Driver Policy.
Step 5 — Enter the driving licence number. This is the 16-character DVLA licence number.
Step 6 — Select the licence type (Full, Provisional, International) and licence category (B, C, D, etc.).
Step 7 — Click Save to create the driver record.
Step 8 — Immediately after saving, upload the driving licence images (see step 2 below). The record is not complete until both images are uploaded.

**2. UPLOADING DRIVING LICENCE IMAGES**

After saving the driver record, you will see upload buttons for Front and Back of licence. Both are required — the compliance record is incomplete without them.

Image quality requirements: the image must be clear, in focus, and legible. The full licence must be visible within the frame. Acceptable formats: JPEG, PNG. Maximum file size: 5MB per image. Photographs taken on a phone are acceptable if they are clear and well-lit. Avoid shadows, glare, or partial cropping.

If an image is rejected, retake it in good lighting with the licence flat on a plain surface. Upload both front and back even if only one side has changed.

**3. CHECKING THE NAMED DRIVER POLICY BEFORE ADDING A DRIVER**

Before adding any driver, always check the Named Driver Policy for that client. Find it in Client Details — Cover Settings — Named Driver Policy. The policy defines:

Minimum age — Drivers below this age cannot be added to the policy.
Acceptable licence types — Only licences matching the listed types are eligible.
Conviction restrictions — Drivers with specified conviction types or point thresholds may be ineligible.

If a driver does not meet all criteria, do not add them to the policy. Record the ineligibility reason in the client's notes and contact the client to advise.

**4. MONITORING EXPIRY DATES**

Driver licence expiry dates must be monitored on an ongoing basis. In the Named Drivers section, use the Expiring Soon filter to see licences expiring within the next 30 days. Contact the client to obtain the renewed licence as soon as an expiry approaches. Upload the new front and back images before the existing licence expires. Set a recurring reminder to check this filter weekly.

**5. REMOVING A DRIVER**

If a driver leaves the fleet or is no longer eligible, open their record and select Deactivate. This removes them from the active policy while retaining their full history for audit purposes. Do not delete driver records — always deactivate.

**6. AUDIT AND COMPLIANCE**

All driver records, licence uploads, and status changes are permanently logged with your username and a timestamp. These records form part of the FCA compliance evidence trail and may be reviewed during insurer due diligence audits.`,
      readingContent: `Named drivers are managed in the **Named Drivers** section of the sidebar. To add a new driver, provide their **full name**, **date of birth**, **licence number**, **licence type**, and **licence category**. After saving the record, you must upload a **front and back image of their driving licence** to complete the compliance record. Licences can be photographed or scanned — both images are required.

Before adding any driver, always check the **Named Driver Policy** for that client. This policy is configured per client in **Client Details → Cover Settings** and sets out the requirements for drivers to be eligible — including minimum age, acceptable licence types, and any conviction restrictions. If a driver does not meet the criteria, they should not be added.

Once added, monitor drivers' **licence expiry dates** from the Named Drivers section. Use the **Expiring Soon** filter to identify licences that are approaching expiry so that renewed licences can be uploaded in good time. Keeping licence records current is a compliance requirement.

All driver records, consent events, and licence uploads are logged in the system. This ensures a clear audit trail for FCA compliance purposes and supports evidence requirements for insurer due diligence.`,
      questions: [
        {
          question: 'After saving a new driver record, what must you do next?',
          options: [
            'Nothing else is required',
            'Upload front and back driving licence images',
            'Send the driver a welcome email',
            'Add them to a site location',
          ],
          correct: 1,
          explanation: 'After creating the driver record, you must upload front and back images of their driving licence to complete the compliance record.',
        },
        {
          question: 'Where is the Named Driver Policy configured?',
          options: [
            'Workstream',
            'Named Drivers page',
            'Client Details → Cover Settings',
            'Dashboard',
          ],
          correct: 2,
          explanation: 'The Named Driver Policy is set per client in Client Details → Cover Settings. It defines eligibility criteria for drivers on that client\'s policy.',
        },
        {
          question: 'What does the Named Driver Policy define?',
          options: [
            'Vehicle cover rates and premiums',
            'Age, licence type, and conviction requirements for drivers',
            'Site location rules',
            'CSV upload column requirements',
          ],
          correct: 1,
          explanation: "The Named Driver Policy sets the eligibility criteria: minimum driver age, acceptable licence types and categories, and conviction restrictions for that client's policy.",
        },
        {
          question: 'Where do you monitor licence expiry dates?',
          options: ['Dashboard', 'Workstream', 'Named Drivers section', 'Messages'],
          correct: 2,
          explanation: 'The Named Drivers section has an Expiring Soon filter to help you identify licences approaching their expiry date.',
        },
        {
          question: 'What information is required to add a named driver?',
          options: [
            'Only their name and date of birth',
            'Full name, date of birth, licence number, licence type and category',
            'Just their licence number',
            'Only their email address',
          ],
          correct: 1,
          explanation: 'To add a driver you need: full name, date of birth, licence number, licence type, and licence category.',
        },
      ],
    },
    {
      index: 4,
      title: 'Reporting & Compliance',
      manualContent: `**FULL TRAINING GUIDE — Reporting and Compliance**

This guide explains how to use Insights & Reports, export data, fulfil your FCA obligations, and use Consent Records as compliance evidence.

**1. INSIGHTS & REPORTS — HOW TO USE**

Step 1 — Go to Insights & Reports in the sidebar.
Step 2 — Set the Date Range. Select a start and end date for the reporting period.
Step 3 — Select the Event Type filter. Options include: All Events, Vehicle Additions, Removals, Reinstatements, Temporary Cover, Change of Vehicle, Status Changes.
Step 4 — Click Apply Filters. The report displays all matching events for your selected period.
Step 5 — Review the data. Look for: any vehicles with unexpected status changes; additions or removals that do not match client instructions; any events without corresponding workstream records.
Step 6 — Export as CSV (for data analysis or insurer submission) or as PDF (for formal records and client sharing).

**2. INSURER MTA SUBMISSION WORKFLOW**

After processing a batch of workstream items, run a report covering that period and export it as CSV. This serves as your submission record to the insurer confirming all mid-term adjustments made. Share the report with the insurer via their preferred channel. Retain a copy in your records. Frequency: submit monthly at minimum, or more frequently if your insurer requires it.

**3. CONSENT RECORDS AS FCA EVIDENCE**

Consent Records (in Admin Toolkit or via the admin view) tracks all GDPR consent events: data privacy acceptances, driver licence upload consents, and Academy completion records. As a Fleet Manager you can view records for your assigned clients. These records serve as evidence that:

- Clients have consented to data processing under GDPR
- Driver data has been collected with appropriate consent
- Users have completed mandatory training

Export consent records periodically and retain them as part of your FCA compliance documentation.

**4. YOUR FCA OBLIGATIONS AS A FLEET MANAGER**

Treating Customers Fairly (TCF) — Always process workstream items promptly and provide clear reasons for rejections. Ensure clients receive timely notifications of all decisions.

Record Keeping — Maintain accurate records of all fleet changes, insurer notifications, and client communications. The portal audit log supports this but you should also retain external correspondence records.

Driver Compliance — Keep all named driver records current, including licence images and expiry dates. Outdated records can invalidate policy cover.

**5. WHAT TO DO IF YOU FIND A DISCREPANCY**

If a report shows a vehicle event that does not match your records: Step 1 — Check the audit log for that vehicle to identify what happened and who processed it. Step 2 — If it was processed in error, contact your line manager or SuperUser. Step 3 — If the insurer needs to be notified of a correction, do so via Messages and retain a record. Step 4 — Document the discrepancy and the resolution in the client's notes.`,
      readingContent: `**Insights & Reports** gives you comprehensive fleet event reporting. You can filter by **date range** and **event type** (vehicle additions, removals, cover changes, and more). Reports can be exported as **CSV** or **PDF** for sharing with clients or for submission to the insurer.

The **Consent Records** section shows all GDPR consent events across your clients — including data privacy acceptances, driver licence upload consents, and **Academy completion records**. As a Fleet Manager, you can view consent records for your assigned clients, which supports evidence requirements for FCA compliance.

Regular reporting is important for two key reasons. First, it supports **insurer mid-term adjustment submissions** — keeping the insurer informed of changes to the fleet in a timely manner. Second, it helps you demonstrate compliance with **FCA obligations**, particularly around Treating Customers Fairly and record-keeping.

Use reports proactively: check for any vehicles with status discrepancies, review recent additions and removals, and ensure that all mid-term adjustments have been properly notified to the insurer. Good reporting habits protect both your clients and your organisation.`,
      questions: [
        {
          question: 'What can you filter reports by in Insights & Reports?',
          options: [
            'Only vehicle type',
            'Date range and event type',
            'Only the driver name',
            'Only the current calendar month',
          ],
          correct: 1,
          explanation: 'Reports can be filtered by date range and event type (e.g. additions, removals, cover changes), giving you flexible reporting options.',
        },
        {
          question: 'What does the Consent Records section show?',
          options: [
            'Vehicle histories only',
            'GDPR consent events including data privacy, driver, and Academy consents',
            'Insurer contact details',
            'Site location records',
          ],
          correct: 1,
          explanation: 'Consent Records tracks all GDPR consent events: data privacy acceptances, driver licence consents, and Academy completion records.',
        },
        {
          question: 'Why is regular reporting important for a Fleet Manager?',
          options: [
            'It is optional and only needed if requested',
            'It supports insurer MTA submissions and FCA compliance obligations',
            'It is only needed for billing purposes',
            'It is only required by senior management',
          ],
          correct: 1,
          explanation: 'Regular reporting supports insurer mid-term adjustment submissions and demonstrates FCA compliance, particularly around record-keeping and Treating Customers Fairly.',
        },
        {
          question: 'How can you export a fleet report?',
          options: [
            'You cannot export reports',
            'As CSV or PDF',
            'Only as an email attachment',
            'Only as a printed document',
          ],
          correct: 1,
          explanation: 'Reports can be exported as CSV (for data analysis) or PDF (for formal records and sharing with clients or insurers).',
        },
        {
          question: 'Where do you find Insights & Reports?',
          options: ['Workstream', 'Admin Tools', 'The sidebar', 'Dashboard only'],
          correct: 2,
          explanation: 'Insights & Reports is accessible from the main sidebar navigation.',
        },
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// TRACK 2 — Portal Administration (Admin, SuperUser roles)
// ─────────────────────────────────────────────────────────────────────────────
const adminTrack: AcademyTrack = {
  trackName: 'Portal Administration',
  role: 'Admin',
  modules: [
    {
      index: 0,
      title: 'System & Client Management',
      manualContent: `**FULL TRAINING GUIDE — System and Client Management**

This guide covers creating and managing client records, configuring cover settings, and ensuring all client data is accurate.

**1. CREATING A NEW CLIENT — STEP BY STEP**

Step 1 — Go to Clients in the sidebar and click Add Client.
Step 2 — Enter the company name, contact name, contact email, and contact phone number.
Step 3 — Enter the Insurer Name. This is the insurance provider underwriting the policy.
Step 4 — Enter the Midterm Email Address. This is where all MTA approval notifications are automatically sent by the system. This field is critical — if it is wrong or missing the insurer will not receive policy change notifications.
Step 5 — Enter the Policy Reference Number. This appears on all generated To Whom It May Concern notifications and insurer documents.
Step 6 — Set the policy start and end dates.
Step 7 — Click Save to create the client record.

**2. THE FIVE CLIENT RECORD TABS**

Vehicles — All fleet vehicles belonging to this client. View, filter, and manage from here.
Sites — All operating locations for this client. Vehicles are linked to sites.
Users — All portal users linked to this client. Manage their access from here.
Cover Settings — Policy configuration including instant cover, vehicle categories, named driver policy, and login video.
Notes — Internal notes about this client. Not visible to the client.

**3. COVER SETTINGS — KEY CONFIGURATIONS**

Instant Cover — When enabled, new vehicle submissions from this client bypass the Workstream and go directly to On Cover. Enable only when the insurer has authorised instant cover for this client.

Vehicle Category — Sets any restrictions or requirements on vehicle types eligible for this client's policy.

Named Driver Policy — Configure minimum age, acceptable licence types, and conviction restrictions for drivers on this client's policy.

Login Video — Upload a custom background video that plays on the client's login screen.

**4. KEEPING CLIENT DATA CURRENT**

Review each client record at least quarterly. Fields that must be kept up to date: contact email and phone; insurer name; midterm email address; policy reference number; policy start and end dates. If any of these are wrong, documents and notifications sent to clients and insurers will contain errors. Set a reminder to audit client records each quarter.

**5. WHAT HAPPENS IF THE MIDTERM EMAIL IS WRONG**

If an MTA is approved and the midterm email is missing or incorrect, the insurer will not receive the notification. This creates a compliance gap. If you discover this after processing: Step 1 — Update the midterm email immediately. Step 2 — Re-send the notification manually by contacting the insurer directly. Step 3 — Log the incident in the client's Notes with the date, the error, and the corrective action taken.`,
      readingContent: `The **Clients** section is the central hub for managing all insured companies on the platform. Each client record contains several tabs: **Vehicles** (all fleet vehicles for that client), **Sites**, **Users**, **Cover Settings**, and **Notes**.

The **Cover Settings** tab is particularly important. From here, you can enable **Instant Cover** for a client (which puts new vehicle submissions directly On Cover without manual workstream approval), set **Vehicle Category** requirements, and manage the **Named Driver Policy**. You can also upload the client's login background video from this tab.

Always keep the following fields up to date in each client record: **contact details**, **insurer name**, **midterm email address**, and **policy reference number**. These details appear on all generated To Whom It May Concern notifications — if they are incorrect, documents sent to clients and insurers will contain errors.

When creating a new client, use accurate policy reference details from the outset. The midterm email is where all MTA approval notifications are automatically sent. If this is wrong or missing, the insurer will not receive policy change notifications. Regularly review client records to ensure all information remains current.`,
      questions: [
        {
          question: "Where do you manage a client's Instant Cover setting?",
          options: [
            'Workstream',
            'Client record → Cover Settings tab',
            'Admin Tools',
            'Users page',
          ],
          correct: 1,
          explanation: "Instant Cover is enabled or disabled in the Cover Settings tab within the individual client's record.",
        },
        {
          question: 'What happens with the policy reference field on a client record?',
          options: [
            'Nothing — it is for internal reference only',
            'It appears on all generated To Whom It May Concern notifications',
            'It is only used for billing',
            'It automatically sets the cover effective date',
          ],
          correct: 1,
          explanation: 'The policy reference appears on all generated To Whom It May Concern notifications sent to clients and insurers, so it must be kept accurate.',
        },
        {
          question: 'Which tab shows all vehicles for a specific client?',
          options: [
            'Cover Settings',
            'Notes',
            'Vehicles tab in the client record',
            'Workstream',
          ],
          correct: 2,
          explanation: "The Vehicles tab within the client's record shows all fleet vehicles belonging to that client.",
        },
        {
          question: "What does enabling 'Instant Cover' do for a client?",
          options: [
            'Removes the requirement for MFA',
            'Puts all new vehicle submissions directly On Cover without manual approval',
            'Automatically approves all driver additions',
            'Skips the DVLA lookup step',
          ],
          correct: 1,
          explanation: 'Instant Cover means new vehicle submissions from that client bypass the Workstream and go directly to On Cover status.',
        },
        {
          question: 'What fields should always be kept up to date in a client record?',
          options: [
            'Nothing specific',
            'Contact details, insurer name, midterm email and policy reference',
            'Only the vehicle count',
            'Only the site names',
          ],
          correct: 1,
          explanation: 'Contact details, insurer name, midterm email, and policy reference are critical — they appear on all generated documents and insurer notifications.',
        },
      ],
    },
    {
      index: 1,
      title: 'Workstream Operations',
      manualContent: `**FULL TRAINING GUIDE — Workstream Operations**

This guide explains the Admin workstream process, escalation procedures, and how to handle complex or edge-case requests.

**1. THE FIVE WORKSTREAM REQUEST TYPES**

Vehicle Addition, Removal, Reinstatement, Temporary Cover (max 30 days), and Change of Vehicle. Review each item's type, client details, and vehicle data before processing.

**2. APPROVING — STEP BY STEP**

Step 1 — Open the item. Review all vehicle data and confirm the client's insurer midterm email is correctly set in their Client Details.
Step 2 — Set the Cover Effective Date. Non-admin roles cannot backdate. As Admin you may backdate with a recorded justification (this is logged in the Backdating Log).
Step 3 — Add clear Processing Notes. Notes appear in the client notification and in the audit record. Be specific and accurate.
Step 4 — Click Approve. The system generates a To Whom It May Concern notification and emails it to the client and the insurer's midterm address. The workstream item resolves and the vehicle status updates.

**3. REJECTING — STEP BY STEP**

Step 1 — Open the item and click Reject.
Step 2 — Enter a specific, actionable Decline Reason.
Step 3 — Confirm the customer was contacted (required under FCA Treating Customers Fairly).
Step 4 — Click Confirm. Client receives a decline notice.

**4. BULK PROCESSING**

Select multiple items, set a shared cover effective date, add notes, and confirm. You can exclude individual items from a bulk action for separate handling.

**5. THE PENDING COUNT CHECK AT LOGOUT**

The portal checks for outstanding workstream items when you log out. If items are pending you will see a warning. Do not dismiss this warning without reviewing the items — unprocessed items mean clients may have vehicles awaiting cover decisions overnight.

**6. ESCALATION AND COMPLEX REQUESTS**

For unusual requests (backdating required, client dispute, policy ambiguity): Step 1 — Do not process until you have clarity. Step 2 — Contact the client via Messages to obtain more information. Step 3 — If a policy decision is needed, escalate to SuperUser or SuperAdmin. Step 4 — Document the escalation and outcome in the workstream item notes before processing.`,
      readingContent: `The Workstream processes five types of client requests: **vehicle additions**, **removals**, **reinstatements**, **temporary cover** (maximum 30 days), and **change-of-vehicle**. Each item shows the requesting client, vehicle details, and any notes from the client.

To **approve** a request, set a cover effective date and add processing notes — a **To Whom It May Concern notification** is automatically generated and emailed to both the client and the **insurer's midterm address**. To **reject** a request, enter a decline reason and confirm that the customer was contacted. **Bulk approve or reject** multiple items simultaneously to process large batches efficiently.

When you log out of the portal, a **pending-count check** runs automatically. If there are outstanding workstream items, you will be prompted before logging out. This ensures that client requests are not overlooked at the end of a working session.

Good workstream discipline is essential: process items promptly, use clear notes, and always set accurate cover effective dates. Inaccurate dates or missing notes can cause problems downstream for both the client and the insurer.`,
      questions: [
        {
          question: 'What is the maximum duration for temporary cover you can approve?',
          options: ['14 days', '7 days', '60 days', '30 days'],
          correct: 3,
          explanation: 'Temporary cover requests have a maximum approved duration of 30 days.',
        },
        {
          question: 'What documents are automatically generated when you approve a workstream item?',
          options: [
            'Nothing is generated automatically',
            'A To Whom It May Concern notification emailed to the client and insurer',
            'Only an internal processing note',
            'A CSV export of the vehicle data',
          ],
          correct: 1,
          explanation: 'On approval, the system generates a To Whom It May Concern notification automatically and emails it to both the client and the insurer\'s midterm address.',
        },
        {
          question: 'What triggers the outstanding-items check?',
          options: [
            'Adding a new client',
            'Logging out of the portal',
            'Generating a report',
            'Starting a bulk upload',
          ],
          correct: 1,
          explanation: 'When you log out, the portal checks for outstanding workstream items and prompts you if any are still pending.',
        },
        {
          question: 'When rejecting a workstream request, what must be recorded?',
          options: [
            'Nothing additional',
            'Decline reason and confirmation that the customer was contacted',
            'Only the vehicle registration',
            'The new proposed cover date',
          ],
          correct: 1,
          explanation: 'Rejections require a decline reason and confirmation the customer was contacted — this supports Treating Customers Fairly obligations.',
        },
{
          question: 'What are the five workstream request types?',
          options: [
            'Add, Remove, Transfer, Insure, Expire',
            'Add, Remove, Reinstate, Temp Cover, Change of Vehicle',
            'Add, Modify, Delete, Archive, Transfer',
            'Submit, Review, Approve, Decline, Archive',
          ],
          correct: 1,
          explanation: 'The five types are: vehicle addition, removal, reinstatement, temporary cover, and change of vehicle.',
        },
      ],
    },
    {
      index: 2,
      title: 'User Management',
      manualContent: `**FULL TRAINING GUIDE — User Management**

This guide covers creating and managing user accounts, understanding role hierarchy, deactivating accounts, and managing lockouts.

**1. CREATING A NEW USER — STEP BY STEP**

Step 1 — Go to Users in the sidebar and click Add User.
Step 2 — Enter a unique username (used to log in), email address, and initial password (minimum 6 characters).
Step 3 — Enter the user's first and last name.
Step 4 — Select the appropriate Role (see role hierarchy below).
Step 5 — For Client, FleetManager, and MTA roles, select a Company from the dropdown. Client users must be linked to a company — without this they cannot see any fleet data. Staff roles (Admin, SuperUser, SuperAdmin) do not require a company link.
Step 6 — Click Create User. The user can now log in with the credentials you set.

**2. ROLE HIERARCHY**

SuperAdmin (highest) — Full system access. Protected — cannot be edited by other users. Created and managed only by other SuperAdmins.
SuperUser — Full admin access including Security & Compliance tools. Can create SuperUser accounts.
Compliance (Admin) — Can manage clients, users, workstream, and reports. Cannot access Security & Compliance.
FleetManager — Processes workstream and manages fleet for assigned clients.
MTA — Read access to client data; processes workstream items.
Client — Manages their own fleet. Limited to their linked company.

Regular Admin (Compliance) accounts cannot create SuperUser or SuperAdmin accounts. Only SuperUser and SuperAdmin can create elevated roles.

**3. DEACTIVATING VS DELETING**

Always deactivate a user rather than deleting. Deactivating removes portal access immediately while retaining all history and audit records — required for FCA compliance. Deleted users may leave gaps in the audit trail.

To deactivate: click the lock icon next to the user. To reactivate: click the unlock icon.

**4. RESETTING A USER'S PASSWORD**

Click the key icon next to the user, enter the new password, and confirm. You do not need the user's current password. Communicate the new password to the user securely (not by email in plain text). Advise them to change it on first login.

**5. MFA AND ACCOUNT LOCKOUT**

MFA is mandatory and cannot be bypassed for Admin, SuperUser, and SuperAdmin roles. These roles always go through MFA regardless of any system settings.

Account lockout activates after 5 consecutive failed login attempts. SuperAdmin and SuperUser are exempt from lockout. To unlock a locked account: find the user on the Users page and click Unlock. The user can then log in again.

**6. AUDIT LOG FOR USER ACTIONS**

All user creation, modification, and deactivation events are logged in the audit trail with your username and a timestamp. Review the audit log periodically to ensure no unauthorised user account changes have occurred.`,
      readingContent: `User accounts are managed from the **Admin** section in the sidebar. You can create users with roles of **Admin**, **Fleet Manager**, **MTA**, or **Client**. When creating a user, you assign them an email address, username, and initial password. **Client users must be linked to a client company** — without this link, they will not be able to see any fleet data.

**SuperAdmin and SuperUser** accounts can also create other SuperUser accounts. Regular Admin accounts cannot create SuperUser or SuperAdmin users.

If a user no longer needs access, **deactivate** their account rather than deleting it. Deactivation removes portal access immediately but retains all of the user's history and audit records, which is important for compliance. You can also **reset any user's password** from the user management page without needing the user's current password.

**MFA (Multi-Factor Authentication)** is mandatory and cannot be bypassed for the **Admin**, **SuperUser**, and **SuperAdmin** roles. This is a security requirement. Client and FleetManager users also use MFA. **Account lockout** activates after **5 consecutive failed login attempts** — you can unlock a locked account from the Users management page. All user activity is recorded in the **audit logs**, which provide a full history of actions taken by each user.`,
      questions: [
        {
          question: 'What must a Client user be linked to when created?',
          options: [
            'No company link is needed',
            'A client company',
            'The Yourshield company',
            'Any company in the system',
          ],
          correct: 1,
          explanation: 'Client users must be linked to a specific client company so they can see and manage that company\'s fleet data.',
        },
        {
          question: 'What happens when you deactivate a user?',
          options: [
            'All their data is permanently deleted',
            'They lose portal access but their history is retained',
            'Their fleet vehicles are automatically removed',
            'Their password is reset automatically',
          ],
          correct: 1,
          explanation: 'Deactivating a user removes their portal access immediately while retaining all their historical records and audit logs — important for compliance.',
        },
        {
          question: 'For which roles is MFA mandatory and cannot be bypassed?',
          options: [
            'Client role only',
            'All roles without exception',
            'Admin, SuperUser and SuperAdmin',
            'SuperAdmin only',
          ],
          correct: 2,
          explanation: 'MFA is mandatory and cannot be bypassed for Admin, SuperUser, and SuperAdmin roles as a security requirement.',
        },
        {
          question: 'What can you do if a user forgets their password?',
          options: [
            "Nothing — they must use the 'Forgot Password' flow",
            'Reset it directly from the Users management page',
            'Delete and recreate their account',
            'Contact Yourshield IT support',
          ],
          correct: 1,
          explanation: 'Admins can reset any user\'s password directly from the Users management page without needing the current password.',
        },
        {
          question: 'Where is user activity recorded?',
          options: ['The Messages section', 'The audit logs', 'The Dashboard only', 'Nowhere'],
          correct: 1,
          explanation: 'All user activity is recorded in the audit logs, providing a full history of every action taken by each user.',
        },
      ],
    },
    {
      index: 3,
      title: 'Security & Compliance Tools',
      manualContent: `**FULL TRAINING GUIDE — Security and Compliance Tools**

This guide explains the four tools in the Security & Compliance section and how to use each one to meet your regulatory obligations.

**1. SYSC 3 ATTESTATION — ANNUAL PROCESS**

FCA SYSC 3 requires senior management to formally confirm that adequate systems and controls are in place. This must be completed once per year.

Step 1 — Go to Security & Compliance in the sidebar and click SYSC 3 Attestation.
Step 2 — Read through all 10 compliance checks carefully. Each check covers a specific control area (data protection, audit logging, MFA, access control, etc.).
Step 3 — Tick each check only when you have verified the control is in place. Do not tick controls that have not been confirmed.
Step 4 — Enter the signatory name and date.
Step 5 — Click Sign and Submit. A signed PDF attestation document is generated and saved to your records.
Step 6 — Retain the PDF as evidence of annual compliance. Past attestations can be downloaded at any time from the Attestation History section.

Set a calendar reminder for 11 months after each submission to ensure the next attestation is completed on time.

**2. TCF MI REPORT — QUARTERLY EXPORT**

The Treating Customers Fairly (TCF) Management Information report provides statistical evidence of TCF compliance. Export it quarterly and retain as FCA documentation.

Step 1 — Go to Security & Compliance and click TCF MI Report.
Step 2 — Set the date range to cover the quarter (e.g. 01 Jan to 31 Mar).
Step 3 — Review the data: look for high rejection rates, delayed processing, or complaints.
Step 4 — Click Export CSV. Save the file with the quarter reference in the filename.
Step 5 — Retain the CSV as quarterly TCF evidence.

**3. MTA REGISTER — INSURER NOTIFICATION LOG**

The MTA Register shows all approved mid-term adjustments, serving as the formal insurer notification record.

Use the filters to find specific adjustments: filter by client, date range, or action type. The register proves that all policy changes were notified to the insurer. Export as needed for insurer submission or audit purposes.

**4. SYSTEM HEALTH — EMAIL MONITORING**

Email delivery is critical: all To Whom It May Concern notifications, client notices, and insurer MTA emails are sent via the email system. System Health monitors for delivery failures.

Step 1 — Check System Health at least weekly.
Step 2 — If failures are flagged, investigate immediately: identify which emails failed, to which recipients, and for which actions.
Step 3 — For client notifications that failed: contact the client directly to confirm they received the relevant information.
Step 4 — For insurer notifications that failed: re-send the notification manually via the insurer's preferred channel. Log the failure and resolution in the client's notes.
Step 5 — Acknowledge the failure in System Health once resolved.`,
      readingContent: `The **Security & Compliance** section (available to SuperUser and SuperAdmin only) contains four key tools that support your regulatory obligations.

**SYSC 3 Attestation** — FCA rules require senior management to confirm that adequate systems and controls are in place. This attestation must be completed **annually**. All **10 checks** must be ticked, then the form is signed and submitted, generating a PDF with the signatory's details. Past attestations can be downloaded as PDFs.

**TCF MI Report** — The Treating Customers Fairly Management Information report provides evidence of TCF compliance. This report should be reviewed and exported as a CSV **quarterly** as documentary evidence for the FCA.

**MTA Register** — This is a filterable log of all approved mid-term adjustments, serving as the formal insurer notification record. Filter by client, date range, and action type.

**System Health** — Monitors the email delivery system and alerts you to any failures. Email delivery is critical because all To Whom It May Concern notifications, client notices, and insurer MTA emails are sent via the email system. Promptly acknowledge and investigate any flagged failures to ensure clients and insurers are receiving their notifications.`,
      questions: [
        {
          question: 'How often must the SYSC 3 attestation be completed?',
          options: ['Monthly', 'Weekly', 'Annually', 'Every 5 years'],
          correct: 2,
          explanation: 'The SYSC 3 attestation is an annual requirement — it must be completed once per year by the designated Senior Manager.',
        },
        {
          question: 'What is the TCF MI Report used for?',
          options: [
            'Approving vehicle additions',
            'Providing quarterly evidence of Treating Customers Fairly compliance',
            'Managing user accounts',
            'Resetting user passwords',
          ],
          correct: 1,
          explanation: 'The TCF MI Report provides quarterly evidence of Treating Customers Fairly compliance, which is required by the FCA.',
        },
        {
          question: 'What does the System Health monitor track?',
          options: [
            'Vehicle cover statuses',
            'Email delivery failures',
            "Driver licence expiry dates",
            'User login counts only',
          ],
          correct: 1,
          explanation: 'System Health monitors the email delivery system for failures. Email is critical for sending To Whom It May Concern notifications, client notices, and insurer MTA emails.',
        },
        {
          question: 'What is the MTA Register?',
          options: [
            'A list of vehicle modifications',
            'A log of all approved insurer notification MTAs',
            'A list of user accounts',
            'A driver compliance record',
          ],
          correct: 1,
          explanation: 'The MTA Register is a filterable log of all approved mid-term adjustments, serving as the insurer notification record.',
        },
        {
          question: 'How many checks must be completed to submit the SYSC 3 attestation?',
          options: ['5', '3', '10', '7'],
          correct: 2,
          explanation: 'All 10 checks must be ticked before the SYSC 3 attestation can be signed and submitted.',
        },
      ],
    },
    {
      index: 4,
      title: 'Data, Auditing & Admin Tools',
      manualContent: `**FULL TRAINING GUIDE — Data, Auditing and Admin Tools**

This guide explains the Admin Tools utilities, consent records, audit logs, and account lockout management.

**1. ADMIN TOOLS — THREE UTILITIES**

Bulk Upload Undo — Reverses a CSV vehicle upload using its batch ID. Use this when an error was made in a bulk import (wrong data, wrong client). Step 1: Go to Admin Tools. Step 2: Enter or find the batch ID. Step 3: Click Undo. All vehicles from that batch are removed. You can then correct the data and re-upload.

Backdating Log — Records every instance where an Admin has set a retrospective (backdated) inception date. Backdating requires a written justification at the time of the action. The log stores: who backdated, which vehicle, the backdated date, and the justification provided. Review this log periodically to ensure backdating is being used appropriately.

Bulk Inception Date Update — Allows you to update inception dates across multiple vehicles simultaneously. Use with care — this action is logged in the audit trail.

**2. CONSENT RECORDS — COMPLIANCE EVIDENCE**

Consent Records shows all GDPR consent events system-wide: data privacy acceptances, driver licence upload consents, and Academy completion records. This is your primary evidence log for data protection compliance.

Export consent records for any period to demonstrate GDPR compliance during FCA reviews or audits. Filter by consent type (GDPR acceptance, driver licence, Academy) or by date range.

**3. AUDIT LOGS — INVESTIGATING ACTIONS**

Audit Logs record every vehicle create, update, and delete action with the user ID and timestamp. How to investigate a specific action: Step 1 — Go to Audit Logs. Step 2 — Filter by vehicle registration or user ID. Step 3 — Review the action log to see exactly what happened, when, and who performed it. This log is the definitive evidence trail for FCA compliance and cannot be altered.

**4. ACCOUNT LOCKOUT MANAGEMENT**

Accounts lock after 5 consecutive failed login attempts. SuperAdmin and SuperUser are exempt from lockout to prevent administrator lock-out scenarios. All other roles are subject to lockout.

To unlock a locked account: Step 1 — Go to Users. Step 2 — Find the locked user (their status will show Locked). Step 3 — Click Unlock. The user can then log in again. Step 4 — If the cause of the lockout was a potential security incident (suspected unauthorised access attempt), log the incident in the Notes section.

**5. GDPR OBLIGATIONS FOR ADMIN USERS**

As an Admin you are responsible for ensuring: user accounts are deactivated (not deleted) when no longer needed; consent records are maintained and exportable; driver data is only uploaded for authorised drivers; and personal data is not retained beyond its required period. Review and clean up inactive user accounts at least quarterly.`,
      readingContent: `**Admin Tools** contains three utilities for advanced fleet management. The **Bulk Upload Undo** tool allows you to reverse a CSV vehicle upload by its batch ID — useful if an error was made in a bulk import. The **Backdating Log** shows all instances where an Admin has set a retrospective inception date for a vehicle, along with the justification provided. The **Bulk Inception Date Update** allows you to update inception dates across multiple vehicles simultaneously.

**Consent Records** shows all GDPR consent events system-wide: data privacy acceptances, driver licence upload consents, and Academy completion records. This is your primary evidence log for data protection compliance.

**Audit Logs** record every vehicle create, update, and delete action with the **user ID and timestamp**. These logs are the definitive evidence trail for FCA compliance and are used to investigate any queries about system actions.

**Account lockout** activates automatically after **5 consecutive failed login attempts**. The SuperAdmin and SuperUser roles are **exempt from lockout** — this prevents authorised administrators from being locked out. To unlock a locked standard user account, use the unlock function on the Users page (available to Admin and above). Encourage users to use the Forgot Password flow if they are unsure of their credentials.`,
      questions: [
        {
          question: 'Where do you find the option to undo a bulk vehicle upload?',
          options: ['Vehicles page', 'Workstream', 'Admin Tools', 'Reports'],
          correct: 2,
          explanation: 'Bulk upload batches can be reversed from Admin Tools using the batch ID.',
        },
        {
          question: 'What triggers an account lockout?',
          options: [
            'Logging out of the portal',
            '5 consecutive failed login attempts',
            'Extended inactivity',
            'Changing your password',
          ],
          correct: 1,
          explanation: 'An account is automatically locked after 5 consecutive failed login attempts as a security measure.',
        },
        {
          question: 'Which roles are exempt from the account lockout policy?',
          options: [
            'All roles are exempt',
            'Client and FleetManager',
            'SuperAdmin and SuperUser',
            'Admin role only',
          ],
          correct: 2,
          explanation: 'SuperAdmin and SuperUser accounts are exempt from lockout to prevent authorised administrators from being locked out of the system.',
        },
        {
          question: 'What does the Backdating Log record?',
          options: [
            'All vehicle additions',
            'Admin-backdated inception dates with justifications',
            'User login times',
            'CSV upload batch IDs only',
          ],
          correct: 1,
          explanation: 'The Backdating Log records every instance where an Admin has set a retrospective (backdated) inception date, along with the justification.',
        },
        {
          question: 'Where are all vehicle changes recorded with user ID and timestamp?',
          options: ['Messages', 'Dashboard', 'Audit Logs', 'Consent Records'],
          correct: 2,
          explanation: 'Audit Logs record every vehicle create, update, and delete action with the user ID and timestamp, forming the definitive compliance evidence trail.',
        },
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// TRACK 3 — Mid-Term Adjustments (MTA role)
// ─────────────────────────────────────────────────────────────────────────────
const mtaTrack: AcademyTrack = {
  trackName: 'Mid-Term Adjustments',
  role: 'MTA',
  modules: [
    {
      index: 0,
      title: 'Portal Navigation for MTA Staff',
      manualContent: `**FULL TRAINING GUIDE — Portal Navigation for MTA Staff**

This guide explains your permissions, what you can and cannot access, and how to use the portal effectively as an MTA team member.

**1. WHAT YOU CAN ACCESS**

As an MTA team member you have access to: Dashboard (fleet-wide vehicle and status overview); Workstream (your primary working area for processing all MTA requests); Clients (read access to all client records, policies, and vehicle lists); Vehicles (read access to all fleet vehicles across all clients); Named Drivers (read access to driver records and licence data); Sites (read access to client operating locations); Insights & Reports (reporting and data export); and Academy (your training programme).

**2. WHAT YOU CANNOT ACCESS**

User Management — Creating, editing, deactivating, or unlocking user accounts is restricted to Admin roles and above. If a user reports an access issue, escalate to an Admin.

Security & Compliance — The SYSC 3 Attestation, TCF MI Report, System Health, and MTA Register sections are restricted to SuperUser and SuperAdmin only.

Client Configuration — You cannot edit client records, change Cover Settings, or modify insurer details. If a client record needs updating, inform an Admin.

**3. USING THE CLIENTS SECTION FOR CONTEXT**

Before processing any workstream item, open the relevant client record in the Clients section and review: the client's cover settings (is Instant Cover enabled?); the insurer midterm email address (is it correctly set?); any notes from previous handlers; and the vehicle list (does the submitted vehicle already exist?).

Processing without reviewing the client context first is the most common cause of processing errors.

**4. AUDIT ACCOUNTABILITY**

Every action you take is permanently logged in the audit record with your username and a precise timestamp: approvals, rejections, notes, and any data you enter. These records cannot be altered and form the official compliance trail. This means:

- Write clear, accurate notes on every workstream item you process
- Set correct cover effective dates — errors here affect the insurer record
- Never process an item if you are unsure — escalate to a senior colleague first

**5. ESCALATING ISSUES**

If you encounter a workstream item you are unsure about (unusual request, data discrepancy, client dispute): do not process it. Add a note explaining the issue and escalate to an Admin or Fleet Manager. It is always better to delay processing than to process incorrectly.`,
      readingContent: `As an **MTA team member**, you have a focused set of permissions designed for processing policy changes efficiently. You have **read access** to client data — including vehicles, named drivers, and sites — and you can view **Insights & Reporting**. Your primary working area is the **Workstream**, where you process mid-term adjustment requests from clients.

There are areas you cannot access: **user account management** is restricted to Admin and above. The **Security & Compliance** section (SYSC 3, TCF Report, System Health) is restricted to SuperUser and SuperAdmin.

The **Dashboard** provides a fleet-wide overview of all vehicles and their current statuses. Use the **Clients** section to review individual client policies, vehicle lists, and cover settings before processing a workstream item — understanding the client context is important for accurate processing.

All actions you take in the portal — approvals, rejections, notes added — are recorded in the **audit log** with your user ID and a timestamp. This means every action is traceable and accountable. Take care to add clear notes and accurate dates when processing workstream items, as these form the compliance record.`,
      questions: [
        {
          question: 'What is the primary working area for MTA staff?',
          options: ['Dashboard', 'Reports', 'Workstream', 'Users'],
          correct: 2,
          explanation: 'The Workstream is the primary working area for MTA staff, where client mid-term adjustment requests are processed.',
        },
        {
          question: 'Which section can MTA staff NOT access?',
          options: ['Vehicles', 'Workstream', 'User management', 'Insights & Reporting'],
          correct: 2,
          explanation: 'User account management is restricted to Admin and above — MTA staff do not have access to create, edit, or deactivate user accounts.',
        },
        {
          question: 'Where are MTA staff actions recorded?',
          options: ['The Messages section', 'The audit log', 'The Dashboard', 'Nowhere'],
          correct: 1,
          explanation: 'All MTA staff actions (approvals, rejections, notes) are recorded in the audit log with user ID and timestamp.',
        },
        {
          question: 'Which section shows individual client policies and vehicle lists?',
          options: ['Workstream', 'Dashboard', 'Clients', 'Consent Records'],
          correct: 2,
          explanation: 'The Clients section allows you to review individual client policies, vehicle lists, and cover settings.',
        },
        {
          question: 'What type of access do MTA staff have to client data?',
          options: [
            'Full write access to all client data',
            'Read access and workstream processing',
            'No access to client data',
            'Access only to their own assigned clients',
          ],
          correct: 1,
          explanation: 'MTA staff have read access to client data (vehicles, drivers, sites) and can process workstream items, but cannot modify client records directly.',
        },
      ],
    },
    {
      index: 1,
      title: 'Understanding MTAs',
      manualContent: `**FULL TRAINING GUIDE — Understanding Mid-Term Adjustments**

This guide provides a comprehensive explanation of what MTAs are, why insurer notification is required, and how each MTA type works.

**1. WHAT IS A MID-TERM ADJUSTMENT?**

A mid-term adjustment (MTA) is any change made to an insurance policy between its renewal dates. MTAs represent changes to the insured risk — adding or removing vehicles, changing cover periods, or swapping registrations. Every MTA must be notified to the insurer promptly. Failure to notify the insurer of a policy change can invalidate cover.

**2. THE FIVE MTA TYPES IN DETAIL**

Vehicle Addition — A new vehicle is being added to a fleet policy. The client submits the vehicle via the portal. MTA staff review the submission and set a cover effective date (the date from which the vehicle is insured). On approval, a To Whom It May Concern notification is automatically sent to the insurer's midterm email address confirming the addition.

Vehicle Removal — An existing vehicle is being taken off cover. The client requests removal with a proposed cover end date. MTA staff review and confirm the end date on approval. The vehicle status changes to Inactive and the insurer is notified via a To Whom It May Concern notification.

Reinstatement — A previously Inactive vehicle is being restored to cover. The client requests reinstatement. MTA staff set a cover restart date on approval. The vehicle returns to On Cover status and the insurer is notified.

Temporary Cover — A vehicle needs cover for a specific date range, with a maximum of 30 days. The client specifies start and end dates. MTA staff confirm the dates on approval. The vehicle is covered for the specified period only. At the end date, cover automatically ends.

Change of Vehicle — One vehicle registration is being replaced by another. The old vehicle is removed from the policy and the new registration is added in its place simultaneously. This is a single MTA that performs two actions. Both are confirmed in one To Whom It May Concern notification to the insurer.

**3. WHY INSURER NOTIFICATION MATTERS**

The insurer must know about every change to the insured risk. The portal handles notification automatically on approval — when you approve an MTA, the system emails a To Whom It May Concern notification to the client and to the insurer's midterm email address. This is the formal insurer notification.

If the insurer's midterm email address is missing or incorrect in the client record, the notification will fail and the insurer will not be informed. Always check this email address before approving any MTA.

**4. FCA COMPLIANCE CONTEXT**

Yourshield Ltd (FCA Firm Reference Number 617848) is the FCA-authorised broker. As an MTA processor you are acting within a regulated environment. Your actions must meet FCA standards for record-keeping, customer treatment, and audit transparency. Processing MTAs accurately and promptly is a regulatory obligation, not just an operational task.`,
      readingContent: `A **Mid-Term Adjustment (MTA)** is any change made to an insurance policy between its renewal dates. On the Myshield platform, there are **five MTA types** that can be submitted by clients and processed by MTA staff.

**(1) Vehicle Addition** — Adding a new vehicle to a fleet policy. The vehicle is submitted by the client and reviewed for approval.

**(2) Vehicle Removal** — Removing a vehicle from active cover. A cover end date is set on approval.

**(3) Reinstatement** — Restoring a previously removed (Inactive) vehicle to active cover. A cover restart date is set.

**(4) Temporary Cover** — Short-term cover for a specific vehicle between defined dates, with a **maximum duration of 30 days**. Useful for seasonal or loaned vehicles.

**(5) Change of Vehicle** — Replacing one registration with another. The old vehicle is removed and the new registration takes its place simultaneously.

Each MTA requires **insurer notification** — on approval, the system automatically generates a **To Whom It May Concern notification** and emails it to both the client and the insurer's midterm email address. This serves as the formal notification to the insurer and the confirmation for the client.`,
      questions: [
        {
          question: 'What does MTA stand for?',
          options: [
            'Motor Transport Administration',
            'Mid-Term Adjustment',
            'Management Transfer Account',
            'Multiple Term Agreement',
          ],
          correct: 1,
          explanation: 'MTA stands for Mid-Term Adjustment — any change to an insurance policy made between renewal dates.',
        },
        {
          question: 'What is the maximum duration for a temporary cover MTA?',
          options: ['7 days', '14 days', '60 days', '30 days'],
          correct: 3,
          explanation: 'Temporary cover MTAs have a maximum duration of 30 days.',
        },
        {
          question: 'A Reinstatement MTA is used to:',
          options: [
            'Add a brand new vehicle to the policy',
            'Restore a previously removed vehicle to cover',
            'Change a vehicle registration number',
            'Request short-term temporary cover',
          ],
          correct: 1,
          explanation: 'A Reinstatement MTA restores a previously Inactive (removed) vehicle back to active cover.',
        },
        {
          question: 'Who receives the To Whom It May Concern notification when an MTA is approved?',
          options: [
            'Only the client',
            "Only the insurer's midterm address",
            "Both the client and the insurer's midterm email address",
            'Only Yourshield internal staff',
          ],
          correct: 2,
          explanation: "On approval, the To Whom It May Concern notification is emailed to both the client and the insurer's midterm email address as the formal insurer notification.",
        },
        {
          question: 'How many MTA types are there in the Myshield platform?',
          options: ['3', '7', '5', '10'],
          correct: 2,
          explanation: 'There are 5 MTA types: vehicle addition, removal, reinstatement, temporary cover, and change of vehicle.',
        },
      ],
    },
    {
      index: 2,
      title: 'Vehicle Additions & Approvals',
      manualContent: `**FULL TRAINING GUIDE — Vehicle Additions and Approvals**

This guide walks through the full approval process for vehicle addition MTAs, including pre-approval checks and how to handle instant cover clients.

**1. PRE-APPROVAL CHECKS**

Before approving any vehicle addition, complete these checks:

Check 1 — Verify the registration number is correct. Compare it with any correspondence from the client.
Check 2 — Confirm the make and model match the registration. If the DVLA lookup returned data, does it match what the client has submitted?
Check 3 — Review the vehicle type and weight class. Are they appropriate for this client's policy?
Check 4 — Check the declared value is reasonable for the vehicle type and age.
Check 5 — Open the client record and confirm the insurer midterm email address is correctly set. If it is missing or wrong, update it before approving.
Check 6 — Check whether this vehicle already exists on the fleet (duplicate registration). If so, do not approve — contact the client to clarify.

If any check fails, contact the client via Messages before processing. Add a note to the workstream item explaining what you checked and what the client confirmed.

**2. SETTING THE COVER EFFECTIVE DATE**

The cover effective date is the single most important field when approving an addition. It is the date from which the vehicle is legally considered insured. This date appears in the To Whom It May Concern notification sent to the insurer and is used for any premium adjustment calculations.

Rules: Use today's date for standard approvals. MTA staff cannot set a date in the past. If a backdated date is required, escalate to an Admin — backdating requires a written justification and is logged.

**3. ADDING APPROVAL NOTES**

Approval notes appear in the client's notification and in the audit record. Always include: the vehicle registration; the action taken; the cover effective date; any special circumstances or conditions. Generic notes (e.g. 'Approved') are not sufficient — be specific.

**4. CLICKING APPROVE**

Once all checks are complete, the cover effective date is set, and notes are added, click Approve. The system will: change the vehicle status to On Cover; generate a To Whom It May Concern notification; email the notification to the client and the insurer's midterm address; and resolve the workstream item.

**5. INSTANT COVER CLIENTS**

If a client has Instant Cover enabled in their Cover Settings, new vehicle submissions bypass the Workstream entirely. The vehicle goes directly to On Cover status and a notification is generated automatically at the point of submission. You will not see these vehicles in the Workstream. Check the client record's Cover Settings to confirm whether Instant Cover is enabled.`,
      readingContent: `When a client submits a vehicle addition, it appears in the **Workstream** with a status of **Pending Cover**. Before approving, review the submission carefully: check the registration, make and model, vehicle type, weight class, and declared value. If anything looks incorrect, contact the client to clarify before processing.

To approve, set a **cover effective date** — this is the date from which the vehicle is considered insured. This date is used for any insurer adjustment calculations. Add clear **approval notes** describing the action taken — these appear in the client's notification email.

Click **Approve**. The system will automatically generate a To Whom It May Concern notification and email it to the client and the insurer's midterm address. The vehicle status changes to **On Cover** and the workstream item is resolved.

If a client has **Instant Cover** enabled in their Cover Settings, new vehicle submissions bypass the Workstream entirely and go directly to **On Cover** status — no manual approval is needed. In this case, the notification is generated at the point of submission. You can see whether a client has Instant Cover enabled in their Client Details.`,
      questions: [
        {
          question: 'What status does a vehicle have when first submitted by a client?',
          options: ['On Cover', 'Inactive', 'Pending Cover', 'Temporary'],
          correct: 2,
          explanation: "A newly submitted vehicle has 'Pending Cover' status, meaning it is awaiting review and approval.",
        },
        {
          question: 'What is the cover effective date?',
          options: [
            'The date the vehicle was manufactured',
            'The date from which the vehicle is considered insured',
            'The date the client submitted the vehicle',
            'The policy renewal date',
          ],
          correct: 1,
          explanation: 'The cover effective date is the date from which the vehicle is insured. It is used for insurer adjustment calculations and appears in the To Whom It May Concern notification.',
        },
        {
          question: 'What is generated automatically when you approve a vehicle addition?',
          options: [
            'A CSV export of vehicle data',
            'A To Whom It May Concern notification emailed to the client and insurer',
            'Only an internal processing note',
            'A new user account for the client',
          ],
          correct: 1,
          explanation: 'On approval, the system automatically generates a To Whom It May Concern notification and emails it to both the client and the insurer\'s midterm address.',
        },
        {
          question: 'What happens if Instant Cover is enabled for a client?',
          options: [
            'All vehicle submissions are rejected automatically',
            'New vehicles go directly On Cover without manual workstream approval',
            'The vehicle stays in Pending Cover indefinitely',
            'The client must approve the vehicle themselves',
          ],
          correct: 1,
          explanation: 'With Instant Cover enabled, new client vehicle submissions bypass the Workstream and go directly to On Cover status automatically.',
        },
        {
          question: 'Where do you find pending vehicle addition submissions?',
          options: ['Dashboard', 'Messages', 'Workstream', 'Clients'],
          correct: 2,
          explanation: 'All pending client vehicle submissions appear in the Workstream for review and approval.',
        },
      ],
    },
    {
      index: 3,
      title: 'Removals, Reinstatements & Temp Cover',
      manualContent: `**FULL TRAINING GUIDE — Removals, Reinstatements, Temporary Cover and Change of Vehicle**

This guide covers the four remaining MTA types in detail, including date handling, pre-approval checks, and what happens after each type is processed.

**1. VEHICLE REMOVAL — FULL PROCESS**

Pre-approval checks: Confirm the registration is correct. Confirm the cover end date requested by the client is appropriate. Check the insurer midterm email is set.

Step 1 — Open the removal workstream item.
Step 2 — Set the Cover End Date. This is the date from which the vehicle is no longer insured. Use the date requested by the client unless there is a specific reason to use a different one.
Step 3 — Add notes explaining the action and the end date.
Step 4 — Click Approve. The vehicle status changes to Inactive. A To Whom It May Concern notification is sent to the client and the insurer's midterm email confirming the removal and end date.

**2. REINSTATEMENT — FULL PROCESS**

Pre-approval checks: Confirm the vehicle is currently Inactive. Confirm the client wants to restore it to active cover. Check the insurer midterm email is set.

Step 1 — Open the reinstatement workstream item.
Step 2 — Set the Cover Restart Date. This is the date from which the vehicle is insured again.
Step 3 — Add notes.
Step 4 — Click Approve. The vehicle returns to On Cover status. A To Whom It May Concern notification confirms the reinstatement and restart date.

**3. TEMPORARY COVER — FULL PROCESS**

Pre-approval checks: Confirm the start and end dates are as requested. Verify the total duration does not exceed 30 days. Check the insurer midterm email is set.

Step 1 — Open the temporary cover workstream item.
Step 2 — Confirm the Cover Start Date and Cover End Date. The system will reject dates spanning more than 30 days.
Step 3 — Add notes confirming the temporary period.
Step 4 — Click Approve. A To Whom It May Concern notification is sent confirming the temporary cover period. At the end date, cover automatically ends and the vehicle returns to its previous status.

**4. CHANGE OF VEHICLE — FULL PROCESS**

Pre-approval checks: Confirm the old registration and new registration are both correct. Confirm the new vehicle details (make, model, value) are accurate. Check the insurer midterm email is set.

Step 1 — Open the change of vehicle workstream item.
Step 2 — Set the Cover Effective Date (when the swap takes effect).
Step 3 — Review both the old vehicle (being removed) and the new vehicle (being added) details.
Step 4 — Add notes documenting both registrations and the reason for the change.
Step 5 — Click Approve. Both changes are processed simultaneously: the old vehicle becomes Inactive and the new vehicle goes On Cover. A single To Whom It May Concern notification documents the complete change.

**5. VERIFYING THE INSURER MIDTERM EMAIL BEFORE EVERY MTA**

Before approving any MTA (of any type), always check that the insurer midterm email address is correctly set in the client record. Open the client record, go to the contact details section, and verify the midterm email. If it is missing or incorrect, update it before processing. If you cannot update it, escalate to an Admin. A missing midterm email means the insurer will not receive the notification — this is a regulatory compliance failure.`,
      readingContent: `Beyond vehicle additions, the Workstream handles four other MTA types that you will regularly process.

**Removal** — When a client requests to remove a vehicle, set a **cover end date** on approval. The vehicle's status changes to **Inactive** and a To Whom It May Concern notification confirming the removal is sent to the client and insurer.

**Reinstatement** — When a client wants to restore an Inactive vehicle, review the request and set a **cover restart date** on approval. The vehicle returns to On Cover status and a notification is generated.

**Temporary Cover** — The client specifies start and end dates (maximum 30 days). On approval, confirm both dates and send the notification. Temporary cover vehicles return to their previous status when the end date is reached.

**Change of Vehicle** — The old vehicle registration is removed from cover and the new registration takes its place. Both changes are reflected in the vehicle records and a single notification documents the change.

Before approving any MTA, always verify that the **client's insurer midterm email address** is correctly set in their Client Details. All MTA approval notifications are automatically sent to this address — if it is missing or incorrect, the insurer will not receive the notification, which creates a compliance gap.`,
      questions: [
        {
          question: "What happens to a vehicle's status when a removal is approved?",
          options: ['Pending Cover', 'On Cover', 'Inactive', 'Temporary'],
          correct: 2,
          explanation: "Approving a removal sets the vehicle's status to Inactive and records the cover end date.",
        },
        {
          question: 'A Change of Vehicle MTA replaces:',
          options: [
            'The client company details',
            "The driver's licence information",
            'One vehicle registration with another',
            'The cover effective date only',
          ],
          correct: 2,
          explanation: 'A Change of Vehicle removes the old registration from the policy and adds the new registration in its place.',
        },
        {
          question: 'What must you verify before approving any MTA?',
          options: [
            "The client's payment status",
            "The client's insurer midterm email address is correctly set",
            "The driver's age",
            'The vehicle colour',
          ],
          correct: 1,
          explanation: "The insurer's midterm email is where all MTA notifications are automatically sent — if incorrect or missing, the insurer will not be notified.",
        },
        {
          question: 'What date is set when approving a Reinstatement?',
          options: [
            'A cover removal date',
            'A cover restart date',
            'A new declared vehicle value',
            'A temporary cover end date',
          ],
          correct: 1,
          explanation: 'When approving a Reinstatement, you set a cover restart date — the date from which the vehicle is back on active cover.',
        },
        {
          question: 'Which MTA type has a maximum approved duration of 30 days?',
          options: ['Vehicle Addition', 'Reinstatement', 'Temporary Cover', 'Change of Vehicle'],
          correct: 2,
          explanation: 'Temporary Cover MTAs are limited to a maximum of 30 days duration.',
        },
      ],
    },
    {
      index: 4,
      title: 'Audit Trail & Compliance',
      manualContent: `**FULL TRAINING GUIDE — Audit Trail and Compliance**

This guide explains the compliance tools available to you, how the audit trail works, and your obligations under FCA regulations.

**1. THE MTA REGISTER — YOUR INSURER NOTIFICATION RECORD**

The MTA Register (in Security & Compliance, visible to SuperUser and SuperAdmin) is a filterable log of every approved mid-term adjustment across all clients. For MTA staff, this register is the formal proof that all policy changes were notified to the insurer.

How to use: Go to Security & Compliance and click MTA Register. Filter by client name to see all MTAs for a specific client. Filter by date range to see a specific period. Filter by action type (Addition, Removal, Reinstatement, Temp Cover, Change of Vehicle). Export to CSV for insurer submission or audit evidence.

**2. CONSENT RECORDS — GDPR COMPLIANCE LOG**

Consent Records tracks all GDPR consent events: data privacy acceptances, driver licence upload consents, and Academy completion records. As an MTA team member you can view these records. They demonstrate that data has been collected and handled lawfully under GDPR. If a client or driver asks about their data consent, you can direct them to the Consent Records view.

**3. AUDIT LOGS — HOW TO USE FOR INVESTIGATIONS**

Audit Logs record every system action with the acting user's ID and a precise timestamp. If a query arises about a vehicle change — such as why a vehicle was approved on a certain date, or who changed a vehicle's status — use the Audit Log to investigate.

Step 1 — Go to Audit Logs (Admin access required; ask an Admin if you need a specific log entry).
Step 2 — Filter by vehicle registration or by your user ID to find the relevant entries.
Step 3 — Review the log entries. Each entry shows the action type, user, timestamp, and any relevant data.
Step 4 — If an error is found in the audit log, do not attempt to correct it yourself. Report it to an Admin immediately.

**4. TCF MI REPORT — QUARTERLY EVIDENCE**

The Treating Customers Fairly Management Information report (Security & Compliance — TCF MI Report) provides quarterly evidence of FCA TCF compliance. This report is generated by SuperUser or SuperAdmin and exported as CSV. As MTA staff, understanding that this report exists helps you appreciate why accuracy and timeliness in your work matters: your processing decisions feed into this evidence.

**5. YOURSHIELD LTD — FCA CONTEXT**

Yourshield Ltd is the FCA-authorised broker behind the Myshield platform. FCA Firm Reference Number: 617848. FCA authorisation means Yourshield operates under legally binding regulatory obligations — Treating Customers Fairly, record-keeping, audit transparency, and timely insurer notification. Your work as an MTA team member directly contributes to meeting these obligations. If you are ever unsure whether a processing decision meets these standards, escalate before proceeding.

**6. WHAT TO DO IF AN ERROR IS DISCOVERED**

If you realise you have made an error in a processed MTA (wrong cover date, wrong vehicle, missed check): Step 1 — Do not attempt to correct it yourself in a way that covers the mistake. Step 2 — Report it to your Admin or line manager immediately, explaining the nature of the error. Step 3 — The Admin will determine the correct corrective action (amendment, re-notification to insurer, client contact). Step 4 — All corrective actions must be documented with notes in the affected workstream record.`,
      readingContent: `The **MTA Register** (in Security & Compliance) is a filterable log of all approved mid-term adjustments across all clients. You can filter by client, date range, and action type. This register serves as the formal **insurer notification log** — it proves that all policy changes were notified to the insurer in a timely manner.

**Consent Records** tracks all GDPR and Academy completion events. These records are important for demonstrating that data has been handled lawfully and that users have completed their required training.

**Audit Logs** record every system action — every vehicle create, update, and delete, every workstream approval or rejection — with the acting user's ID and a precise timestamp. These logs are the evidence trail for **FCA compliance** and can be used to investigate queries or complaints.

The **TCF MI Report** (Treating Customers Fairly Management Information) provides statistical evidence that clients are being treated fairly. It should be reviewed and exported as a CSV **quarterly** from Security & Compliance → TCF MI Report.

Yourshield Ltd is the FCA-authorised broker behind the Myshield platform. Yourshield's **FCA Firm Reference Number is 617848**. This number appears on regulatory correspondence and is important for compliance documentation. FCA authorisation means Yourshield operates under binding regulatory obligations, which flow through to how you operate the Myshield platform.`,
      questions: [
        {
          question: 'What is the purpose of the MTA Register?',
          options: [
            'Recording vehicle faults and defects',
            'Showing all approved MTAs as the insurer notification log',
            'Managing user account permissions',
            'Tracking driver licence expiry dates',
          ],
          correct: 1,
          explanation: 'The MTA Register is the formal insurer notification log — it shows all approved MTAs and proves that policy changes were communicated to the insurer.',
        },
        {
          question: 'Where do you generate the TCF MI Report?',
          options: [
            'Admin Tools',
            'Dashboard',
            'Security & Compliance → TCF MI Report',
            'Workstream',
          ],
          correct: 2,
          explanation: 'The TCF MI Report is generated from Security & Compliance → TCF MI Report and should be exported as CSV quarterly.',
        },
        {
          question: 'How often should the TCF MI Report be reviewed?',
          options: ['Daily', 'Annually', 'Quarterly', 'Monthly'],
          correct: 2,
          explanation: 'The TCF MI Report should be reviewed quarterly and the CSV exported as evidence of Treating Customers Fairly compliance.',
        },
        {
          question: 'What does the Audit Log record?',
          options: [
            'Vehicle additions only',
            'Every system action with user ID and timestamp',
            'Login events only',
            'Workstream approvals only',
          ],
          correct: 1,
          explanation: 'The Audit Log records every system action — creates, updates, deletes, approvals, rejections — each with the acting user\'s ID and timestamp.',
        },
        {
          question: "What is Yourshield Ltd's FCA Firm Reference Number?",
          options: ['123456', '999999', '617848', '08693210'],
          correct: 2,
          explanation: 'FRN 617848 is Yourshield Ltd\'s FCA authorisation number, important for compliance documentation and regulatory correspondence.',
        },
      ],
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// Exported data
// ─────────────────────────────────────────────────────────────────────────────

/** All four academy tracks in order: Client, FleetManager, Admin/SuperUser, MTA */
export const TRACKS: AcademyTrack[] = [clientTrack, fleetManagerTrack, adminTrack, mtaTrack];

/** Returns the appropriate track for a given role, or null for SuperAdmin (browse-only). */
export function getTrackForRole(role: string): AcademyTrack | null {
  switch (role) {
    case 'Client':       return TRACKS[0];
    case 'FleetManager': return TRACKS[1];
    case 'Admin':
    case 'SuperUser':    return TRACKS[2];
    case 'MTA':          return TRACKS[3];
    default:             return null; // SuperAdmin → browse only
  }
}
